import type { IndexableType, Table } from 'dexie'
import Dexie from 'dexie'
import { isAxiosError } from 'axios'
import { assign, clone, isObject, uniqBy } from 'lodash-es'
import Net from './net'
import utils from './utils'
import LinkedCustomer, { LinkedCustomerAllocationGroup } from '@/models/linkedCustomer'
import Article from '@/models/article'
import RequestModel from '@/models/request'
import type ArticleDeliverDate from '@/models/articleDeliveryDate'
import type { Price } from '@/models/articlePrice'
import type ArticlePrice from '@/models/articlePrice'
import type ArticleSize from '@/models/articleSize'
import type ArticleSegmentation from '@/models/articleSegmentation'
import Resource from '@/models/resource'
import DropReason from '@/models/dropReason'
import type { Segmentation } from '@/models/customerSegmentation'
import CustomerSegmentation from '@/models/customerSegmentation'
import CatalogTreeDef from '@/models/catalogTreeDef'
import CatalogDetails from '@/models/catalogDetails'
import MyArticle from '@/models/myArticle'
import type CatalogPriceGroup from '@/models/catalogPriceGroup'
import FavoriteTag from '@/models/favoriteTag'
import SimpleFavoriteTag from '@/models/simpleFavoriteTag'
import ResourceType from '@/models/resourceType'
import RelationType from '@/models/objectRelation/relationType'
import ObjectRelation from '@/models/objectRelation/objectRelation'
import Allocation from '@/models/allocation'
import SavedView from '@/models/savedView'
import ColorPallet from '@/models/colorPallet'
import SellerDetails from '@/models/sellerDetails'
import Whiteboard from '@/models/whiteboard'
import NotificationModel from '@/models/notificationModel'
import Job from '@/models/job'
import { getResourceTypes } from '@/api/t1/resource'
import { getObjectRelations, getRelationTypes } from '@/api/t1/objectRelation'
import { getCustomerAllocationGroups } from '@/api/t1/customer'
import { getArticleDropReasons, getArticlesCount, getArticlesStream, getCatalogArticlesTree } from '@/api/t1/article'
import { createUpdateFavorite, updateFavorite, updateFavoritesStatus } from '@/api/t1/favorite'
import type { UpdateFavoritesStatusModel } from '@/api/t1/model/favoriteModel'
import type DuneAsset from '@/models/duneAsset'
import { appConstants, requestConstants } from '@/models/constants'
import { type FilterCriteria, FilterCriteriaMode } from '@/models/filterCriteria'
import type CatalogAttribute from '@/models/catalogAttribute'
import { AttributeType } from '@/models/catalogAttribute'
import type { ArticleFilterModel, ArticleModel, ArticleStateModel, GetCatalogArticlesTreeModel, ModelArticleModel } from '@/api/t1/model/articleModel'
import type { ICustomersLocations } from '@/api/t1/model/orderModel'
import CustomerLocation from '@/models/CustomerLocation'
import { getCatalogDetails } from '@/api/t1/catalog'
import UserPreferences from '@/models/userPreferences'
import type ArticleForm from '@/models/articleForm'
import { getDatasetDetails } from '@/api/t1/datasets'

export type Entity = 'Requests' | 'Resources' | 'MyFavoriteTags' | 'Allocations' | 'SavedViews' | 'ColorPallets' | 'Whiteboards' | 'Locations' | 'CustomerSegmentations' | 'Notifications'
// type DBTable = 'catalogs' | 'singleton' | 'catalogDetails' | 'catalogTreeDef' | 'linkedCustomers' | 'articles' | 'resources' | 'dropReasons' | 'favoriteTags' | 'allocations' | 'savedViews' | 'colorPallets' | 'whiteboards' | 'locations'

interface ArticleEntitiesParams { prices: ArticlePrice[], sizes: ArticleSize[], segmentations: ArticleSegmentation[], deliveryDates: ArticleDeliverDate[] }

export const ARTICLES_LIMIT = 100000
export const REQUESTS_LIMIT = 1000
const BLANK_TREE_NODE_LABEL = '(Others)'
export class DB extends Dexie {
  catalogs: Table<ICatalog, IndexableType>
  singleton: Table<any, IndexableType>
  catalogDetails: Table<any, IndexableType>
  sellerDetails: Table<any, IndexableType>
  catalogTreeDef: Table<CatalogTreeDef, IndexableType>
  linkedCustomers: Table<LinkedCustomer, IndexableType>
  articles: Table<Article, IndexableType>
  requests: Table<RequestModel, IndexableType>
  // prices: Table<ArticlePrice, IndexableType>
  // sizes: Table<ArticleSize, IndexableType>
  // deliveryDates: Table<ArticleDeliverDate, IndexableType>
  // segmentations: Table<ArticleSegmentation, IndexableType>
  resources: Table<Resource, IndexableType>
  dropReasons: Table<DropReason, IndexableType>
  resourceTypes: Table<ResourceType, IndexableType>
  relationTypes: Table<RelationType, IndexableType>
  objectRelations: Table<ObjectRelation, IndexableType>
  customerSegmentations: Table<CustomerSegmentation, IndexableType>
  allocations: Table<Allocation, IndexableType>
  savedViews: Table<SavedView, IndexableType>
  colorPallets: Table<ColorPallet, IndexableType>
  whiteboards: Table<Whiteboard, IndexableType>
  jobs: Table<Job, IndexableType>
  locations: Table<CustomerLocation, IndexableType>
  userPreferences: Table<UserPreferences, IndexableType>
  trackChanges: boolean
  notifications: Table<NotificationModel, IndexableType>
  myFavorites: Table<FavoriteTag, IndexableType>
  sharedFavorites: Table<FavoriteTag, IndexableType>
  articleForms: Table<ArticleForm, IndexableType>

  private getUrl(entity: Entity, catalog: CatalogDetails, since?: string) {
    const catalogCode = catalog.CatalogCode
    const u = since ? `u=${since}` : ''
    switch (entity) {
      case 'Requests':
        return `/catalogs/${catalogCode}/requests?requestversion=${requestConstants.requestVersion}&extendedresults=true&${u}`
      case 'Resources':
        return `/livefeed/catalogs/${catalogCode}/resources?${u}`
      case 'MyFavoriteTags':
        return `/catalogs/${catalogCode}/myfavorites?${u}`
      case 'Allocations':
        return `/livefeed/catalogs/${catalogCode}/allocations?${u}`
      case 'SavedViews':
        return `/catalogs/${catalogCode}/mysavedviews?${u}`
      case 'ColorPallets':
        return `/catalogs/${catalogCode}/colors?${u}`
      case 'Whiteboards':
        return `/catalogs/${catalogCode}/mywhiteboards?${u}`
      case 'Locations':
        return `/livefeed/catalogs/${catalogCode}/customerlocations?${u}`
      case 'CustomerSegmentations':
        return `/livefeed/catalogs/${catalogCode}/customersegmentations?${u}`
      default:
        throw new Error('Invalid Entity')
    }
  }

  constructor(databaseName: string) {
    super(databaseName)
    this.version(63).stores({
      sellerDetails: 'AccountId',
      catalogs: 'Id, CatalogCode, Status, Visibility, DataSourceTypeId',
      singleton: 'name',
      catalogDetails: 'CatalogCode, UpdatedDate',
      catalogTreeDef: '[CatalogCode+Id], CatalogCode, Status',
      linkedCustomers: '[CatalogCode+CustomerId], CatalogCode, Status',
      locations: 'Id, CustomerId, Status, AccountId, [AccountId+CustomerId]',
      articles: '[CatalogCode+Id], [CatalogCode+Status], [CatalogCode+ArticleNumber], [CatalogCode+ModelNumber], [CatalogCode+Status+ModelNumber], [CatalogCode+ModelId], [CatalogCode+Status+ModelId], CatalogCode, Id, ModelId, ArticleNumber, ModelNumber, Status',
      // prices: '[CatalogCode+Id], CatalogCode, Id',
      // sizes: '[CatalogCode+Id], CatalogCode',
      // deliveryDates: '[CatalogCode+Id], CatalogCode, Id',
      // segmentations: '[CatalogCode+Id], CatalogCode, Id',
      resourceTypes: 'Id, Type',
      resources: 'Id, CatalogCode, [CatalogCode+ResourceId],[CatalogCode+ResourceTypeId], [CatalogCode+ResourceName+ResourceTypeId], *Articles',
      dropReasons: '[SellerAccountId+Id], SellerAccountId, Id',
      relationTypes: 'Id, CatalogCode, Status',
      objectRelations: 'Id, CatalogCode, [CatalogCode+ObjectId+EntityId+IsRequestArticle]',
      customerSegmentations: '[CatalogCode+Id], CatalogCode, Id',
      myFavorites: '&[CatalogCode+UserId+CreatedByUserName+Tag+CustomerId], [CatalogCode+UserId+Tag+CustomerId],[CatalogCode+UserId+CreatedByUserName+Status+CustomerId], [CatalogCode+Status], [CatalogCode+UserId+Status+CustomerId], Id, *Articles',
      sharedFavorites: '&[CatalogCode+UserId+Id], Id, CatalogCode, [CatalogCode+UserId], *Articles',
      allocations: '[CatalogCode+Id], CatalogCode, Status, *Articles',
      savedViews: '[CatalogCode+Id], Id, CatalogCode, Status, [CatalogCode+CreatedBy+IsCatalogLevel+Status], [CatalogCode+IsCatalogLevel+Status]',
      colorPallets: '[CatalogCode+Id], Id, CatalogCode, Name, Status, *ColorIds',
      whiteboards: '[CatalogCode+Id], [CatalogCode+CreatedBy], Id, CatalogCode, Status',
      requests: '[CatalogCode+Id], [CatalogCode+SourceModelId], [CatalogCode+SourceArticleId], [CatalogCode+ObjectId], [CatalogCode+Status], [CatalogCode+Status+IsCreateArticleRequest], [CatalogCode+IsCreateArticleRequest], CatalogCode, Id, SourceModelId, SourceArticleId',
      jobs: '[CatalogCode+Id], [CatalogCode+UserId], Id, CatalogCode, Type, UserId, Status, CreatedDate',
      notifications: 'Id, UserId, UpdatedDate, [UserId+NotificationType], [UserId+CatalogCode+NotificationType]',
      userPreferences: 'userId',
    }).upgrade((tx) => {
      return Promise.all([
        tx.table('singleton').clear(),
        tx.table('sellerDetails').clear(),
        tx.table('catalogs').clear(),
        tx.table('catalogDetails').clear(),
        tx.table('catalogTreeDef').clear(),
        tx.table('linkedCustomers').clear(),
        tx.table('locations').clear(),
        tx.table('articles').clear(),
        tx.table('resourceTypes').clear(),
        tx.table('resources').clear(),
        tx.table('dropReasons').clear(),
        tx.table('relationTypes').clear(),
        tx.table('objectRelations').clear(),
        tx.table('customerSegmentations').clear(),
        tx.table('myFavorites').clear(),
        tx.table('sharedFavorites').clear(),
        tx.table('allocations').clear(),
        tx.table('savedViews').clear(),
        tx.table('colorPallets').clear(),
        tx.table('whiteboards').clear(),
        tx.table('requests').clear(),
        tx.table('jobs').clear(),
        tx.table('notifications').clear(),
        tx.table('userPreferences').clear(),
      ])
    })
    this.version(66).stores({
      myFavorites: '&[CatalogCode+UserId+CreatedByUserName+Tag+CustomerId], [CatalogCode+UserId+Tag+CustomerId],[CatalogCode+UserId+CreatedByUserName+Status+CustomerId], [CatalogCode+Status], [CatalogCode+UserId+Status+CustomerId], Id, *Articles',
      sharedFavorites: '&[CatalogCode+UserId+Id], Id, CatalogCode, [CatalogCode+UserId], [CatalogCode+Status+UserId], *Articles',
      notifications: 'Id, UserId, UpdatedDate, [UserId+Status], [UserId+NotificationType], [UserId+CatalogCode+NotificationType]',
      articleForms: '[CatalogCode+ArticleId+UserId+Type], [CatalogCode+ArticleId+UserId], [CatalogCode+ArticleId]',
    })
    this.catalogs = this.table('catalogs')
    this.singleton = this.table('singleton')
    this.catalogDetails = this.table('catalogDetails')
    this.sellerDetails = this.table('sellerDetails')
    this.catalogTreeDef = this.table('catalogTreeDef')
    this.linkedCustomers = this.table('linkedCustomers')
    this.articles = this.table('articles')
    this.requests = this.table('requests')
    // this.prices = this.table('prices')
    // this.sizes = this.table('sizes')
    // this.deliveryDates = this.table('deliveryDates')
    // this.segmentations = this.table('segmentations')
    this.resources = this.table('resources')
    this.dropReasons = this.table('dropReasons')
    this.resourceTypes = this.table('resourceTypes')
    this.relationTypes = this.table('relationTypes')
    this.objectRelations = this.table('objectRelations')
    this.customerSegmentations = this.table('customerSegmentations')
    this.allocations = this.table('allocations')
    this.savedViews = this.table('savedViews')
    this.colorPallets = this.table('colorPallets')
    this.whiteboards = this.table('whiteboards')
    this.jobs = this.table('jobs')
    this.locations = this.table('locations')
    this.userPreferences = this.table('userPreferences')
    this.trackChanges = false
    this.notifications = this.table('notifications')
    this.myFavorites = this.table('myFavorites')
    this.sharedFavorites = this.table('sharedFavorites')
    this.articleForms = this.table('articleForms')

    this.catalogDetails.mapToClass(CatalogDetails)
    this.myFavorites.mapToClass(FavoriteTag)
    this.savedViews.mapToClass(SavedView)
    this.colorPallets.mapToClass(ColorPallet)
    this.whiteboards.mapToClass(Whiteboard)
    this.jobs.mapToClass(Job)
    this.requests.mapToClass(RequestModel)
    this.userPreferences.mapToClass(UserPreferences)
    this.notifications.mapToClass(NotificationModel)
    this.sharedFavorites.mapToClass(FavoriteTag)
  }

  async loadEntitiesFirstTime(entitiesToLoad: Entity[], catalog: CatalogDetails, userId?: number, isViewUnAssortedArticlesRestricted?: boolean, progress?: (entity: Entity, progress: number) => void) {
    const promises: Promise<void | number>[] = []

    console.log('loading entities')
    // Load other entities
    entitiesToLoad.forEach((entity) => {
      promises.push(this.loadEntitySinceLastCall(entity, catalog, userId, isViewUnAssortedArticlesRestricted, (ip) => {
        if (progress) { progress(entity, ip) }
      }))
    })

    await Promise.all(promises)
  }

  async loadEntitySinceLastCall(entity: Entity, catalog: CatalogDetails, userId?: number, isViewUnAssortedArticlesRestricted?: boolean, progress?: (progress: number) => void): Promise<void> {
    const nameKey = utils.isDefined(userId) && (entity === 'SavedViews' || entity === 'MyFavoriteTags')
      ? `lc-${catalog.CatalogCode}-u-${userId}-${entity}`
      : `lc-${catalog.CatalogCode}-${entity}`
    let lastCall = await this.singleton.get(nameKey)
    let since: string | undefined
    let firstTime = true
    if (lastCall && lastCall.dt) {
      firstTime = false
      since = lastCall.dt
    }
    else {
      lastCall = { name: nameKey }
    }
    const url = this.getUrl(entity, catalog, since)
    if (!url) {
      console.warn('Unable to get URL for entity', entity)
      return
    }

    const res = await Net.t1.get(url)
    console.time(`${entity}-${catalog.CatalogCode}`)

    switch (entity) {
      case 'Requests': {
        if (res.data && res.data.length) {
          const requestsSince: string = utils.getMaxUpdatedDate(res.data)
          if (requestsSince !== null) {
            lastCall.dt = requestsSince
          }
          const sourceArticleIds: number[] = []
          const sourceModelIds: number[] = []
          const data = res.data.map((item) => {
            if (item.SourceArticleId) {
              sourceArticleIds.push(item.SourceArticleId)
            }
            else if (item.SourceModelId) {
              sourceModelIds.push(item.SourceModelId)
            }
            return new RequestModel(item)
          })
          await this.requests.bulkPut(data)

          const linkedCatalogDetails: Record<number, CatalogDetails> = {}
          if (catalog.LinkedCatalogList?.length) {
            for (const linkedCatalog of catalog.LinkedCatalogList) {
              linkedCatalogDetails[linkedCatalog.CatalogCode] = await this.getCatalogDetails(linkedCatalog.CatalogCode, false)
            }
          }

          const requestArticles: Article[] = []

          const seasonFilter: ArticleFilterModel | null = (catalog._LinkedCatalogCodes.length) ? { attribute: '_Seasons', mode: FilterCriteriaMode.multiString, value: catalog._LinkedCatalogCodes, exclude: false } : null

          if (sourceArticleIds.length) {
            const filters: ArticleFilterModel[] = []
            if (seasonFilter) {
              filters.push(seasonFilter)
            }
            filters.push({ attribute: 'ArticleId', mode: FilterCriteriaMode.multiString, value: sourceArticleIds, exclude: false })
            const responseArticles = await getArticlesStream(catalog.CatalogCode, { body: { filters } })
            if (responseArticles?.length) {
              responseArticles.forEach((responseArticle: any) => {
                const catalogDetails = linkedCatalogDetails[responseArticle.CatalogCode] || catalog
                const article = new Article(catalogDetails.CatalogCode, catalogDetails.AssignedCatalogAttributes, responseArticle, catalogDetails.DataSourceTypeId, [])
                requestArticles.push(article)
              })
            }
          }

          if (sourceModelIds.length) {
            const filters: ArticleFilterModel[] = []
            if (seasonFilter) {
              filters.push(seasonFilter)
            }
            filters.push({ attribute: 'ModelId', mode: FilterCriteriaMode.multiString, value: sourceModelIds, exclude: false })
            const responseArticles = await getArticlesStream(catalog.CatalogCode, { body: { filters } })
            if (responseArticles?.length) {
              responseArticles.forEach((responseArticle: any) => {
                const catalogDetails = linkedCatalogDetails[responseArticle.CatalogCode] || catalog
                const article = new Article(catalogDetails.CatalogCode, catalogDetails.AssignedCatalogAttributes, responseArticle, catalogDetails.DataSourceTypeId, [])
                requestArticles.push(article)
              })
            }
          }

          if (requestArticles.length) {
            await this.articles.bulkPut(requestArticles)
          }
        }
        break
      }

      case 'CustomerSegmentations': {
        if (res.data && res.data.length) {
          const CustomerSegmentationsSince: string = utils.getMaxUpdatedDate(res.data, 'UpdatedDate', 'SegmentationList')
          if (CustomerSegmentationsSince !== null) {
            lastCall.dt = CustomerSegmentationsSince
          }
          const data = res.data.map((cs: any) => new CustomerSegmentation(catalog.CatalogCode, cs.CustomerId, cs.SegmentationList))
          if (firstTime) {
            await this.customerSegmentations.bulkPut(data)
          }
          else {
            const dbCustomerSegmentations = await this.customerSegmentations.where('[CatalogCode+Id]').anyOf(data.map((itm: any) => [catalog.CatalogCode, itm.Id])).toArray()
            const dbCustomerSegmentationsByCustomerId = dbCustomerSegmentations.reduce((acu, art) => (acu[art.Id] = art) && acu, {})
            for (const itm of data as CustomerSegmentation[]) {
              if (utils.isDefined(dbCustomerSegmentationsByCustomerId[itm.Id])) {
                // If new segmentations exist in the response, reset existing ones and add the new ones
                const dbSegmentations = dbCustomerSegmentationsByCustomerId[itm.Id].Segmentations
                if (itm.Segmentations && Object.keys(itm.Segmentations).length > 0) {
                  itm.Segmentations.forEach((segmentation) => {
                    const indexValue = dbSegmentations.findIndex(dbSeg => dbSeg.Id === segmentation.Id)
                    if (indexValue !== -1) {
                      dbSegmentations[indexValue] = segmentation
                    }
                    else {
                      dbSegmentations.push(segmentation)
                    }
                  })
                  await this.customerSegmentations.update([catalog.CatalogCode, itm.Id], { Segmentations: dbSegmentations })
                }
              }
              else {
                // If it's the first time or no existing segmentations, assign the new segmentations'
                await this.customerSegmentations.put(itm)
              }
            }
          }
        }
        break
      }

      case 'Resources': {
        if (res.data && res.data.length) {
          const resourcesSince: string = utils.getMaxUpdatedDate(res.data)
          if (resourcesSince !== null) {
            lastCall.dt = resourcesSince
          }
          const data = res.data.map((itm: any) => new Resource(catalog.CatalogCode, itm))
          if (firstTime) {
            await this.resources.where('CatalogCode').equals(catalog.CatalogCode).delete()
            await this.resources.bulkAdd(data)
          }
          else {
            await this.resources.bulkPut(data)
          }
        }
        break
      }

      case 'MyFavoriteTags': {
        if (res.data && res.data.length) {
          const favoritesSince: string = utils.getMaxUpdatedDate(res.data)
          if (favoritesSince !== null) {
            lastCall.dt = favoritesSince
          }
          Promise.all((res.data as Array<any>).map(async (itm) => {
            const remoteTag = new FavoriteTag(catalog.CatalogCode, userId!, itm)
            if (remoteTag.CreatedBy === userId) { // mytags
              const localTag = await this.myFavorites.get({ CatalogCode: catalog.CatalogCode, Tag: itm.Tag, UserId: userId, CustomerId: remoteTag.CustomerId })
              if (!localTag || (localTag.LocalUpdatedDate < new Date(remoteTag.UpdatedDate!))) {
                const existingTag = await this.myFavorites.where('Id').equals(itm.Id).filter(tag => tag.UserId === userId && tag.CatalogCode === catalog.CatalogCode).first()
                if (existingTag) {
                  this.myFavorites.where('[CatalogCode+UserId+Tag+CustomerId]').equals([catalog.CatalogCode, userId!, existingTag.Tag, existingTag.CustomerId]).delete()
                }
                this.myFavorites.put(remoteTag)
              }
            }
            else { // sharedtags
              const sharedTag = await this.sharedFavorites.get({
                CatalogCode: catalog.CatalogCode,
                UserId: userId,
                Id: remoteTag.Id,
              })

              if (!remoteTag.Status) {
                if (sharedTag) {
                  await this.sharedFavorites.where('[CatalogCode+UserId+Id]')
                    .equals([catalog.CatalogCode, userId!, sharedTag.Id])
                    .delete()
                }
              }
              else {
                if (!sharedTag) {
                  if (remoteTag.SharedUsers?.length || remoteTag.SharedUsersGroups?.length) {
                    await this.sharedFavorites.put(remoteTag)
                  }
                }
                else {
                  await this.sharedFavorites.where('[CatalogCode+UserId+Id]')
                    .equals([catalog.CatalogCode, userId!, sharedTag.Id])
                    .delete()
                  if (remoteTag.SharedUsers?.length || remoteTag.SharedUsersGroups?.length) {
                    await this.sharedFavorites.put(remoteTag)
                  }
                }
              }
            }
          }))
        }
        break
      }

      case 'Allocations': {
        if (res.data && res.data.length) {
          const allocationsSince: string = utils.getMaxUpdatedDate(res.data)
          if (allocationsSince !== null) {
            lastCall.dt = allocationsSince
          }
          const data = res.data.map((al: any) => new Allocation(catalog.CatalogCode, al))
          await this.allocations.bulkPut(data)
          await this.allocations.where('Status').equals(0).delete()
        }
        break
      }

      case 'SavedViews': {
        if (res.data && res.data.length) {
          const savedViewsSince: string = utils.getMaxUpdatedDate(res.data)
          if (savedViewsSince !== null) {
            lastCall.dt = savedViewsSince
          }
          const data = res.data.map((sv: any) => new SavedView(catalog.CatalogCode, sv))
          await this.savedViews.bulkPut(data)
          await this.savedViews.where('Status').equals(0).delete()
        }
        break
      }

      case 'ColorPallets': {
        if (res.data && res.data.length) {
          const colorPalettesSince: string = utils.getMaxUpdatedDate(res.data)
          if (colorPalettesSince !== null) {
            lastCall.dt = colorPalettesSince
          }
          const data = res.data.map((cp: any) => new ColorPallet(catalog.CatalogCode, cp))
          await this.colorPallets.bulkPut(data)
          await this.colorPallets.where('Status').equals(0).delete()
        }
        break
      }

      case 'Whiteboards': {
        if (res.data && res.data.length) {
          const whiteboardsSince: string = utils.getMaxUpdatedDate(res.data)
          if (whiteboardsSince !== null) {
            lastCall.dt = whiteboardsSince
          }
          const data = res.data.map((wb: any) => new Whiteboard(catalog.CatalogCode, wb))
          await this.whiteboards.bulkPut(data)
          await this.whiteboards.where('Status').equals(0).delete()
        }
        break
      }

      case 'Locations': {
        if (res.data && res.data.length) {
          const locationsSince: string = utils.getMaxUpdatedDate(res.data, undefined, 'LocationList')
          if (locationsSince !== null) {
            lastCall.dt = locationsSince
          }
          const customersToDelete: Array<number> = []
          const data: Array<CustomerLocation> = [];
          (res.data as Array<ICustomersLocations>).forEach((customerLocations) => {
            if (customerLocations.LocationList == null || !customerLocations.LocationList.length) {
              customersToDelete.push(customerLocations.CustomerId)
            }
            customerLocations.LocationList.forEach((location) => {
              data.push(new CustomerLocation(catalog.AccountId, { ...location, CustomerNumber: customerLocations.CustomerNumber, CustomerId: customerLocations.CustomerId }))
            })
          })
          await this.locations.bulkPut(data)
          await this.locations.where('Status').equals(0).delete()
          // customerId is unique in our system across all sellers and location and customers are tied to seller (not catalog)
          await this.locations.where('CustomerId').anyOf(customersToDelete).delete()
        }
        break
      }

      default:
        throw new Error('Invalid Entity')
    }
    console.timeEnd(`${entity}-${catalog.CatalogCode}`)
    await this.singleton.put(lastCall)
    if (progress) { progress(100) }
  }

  async getMyNotification(userId, entity = 'Notifications') {
    const nameKey = `u-${userId}-${entity}`
    let lastCall = await this.singleton.get(nameKey)
    let since: string | undefined
    if (lastCall && lastCall.dt) {
      since = lastCall.dt
    }
    else {
      lastCall = { name: nameKey }
    }
    const u = since ? `sdate=${since}` : ''
    const res = await Net.t1.get(`/mynotifications?notificationtypeid=6&${u}`)
    if (res.data && res.data.length) {
      const notificationSince: string = utils.getMaxUpdatedDate(res.data)
      if (notificationSince !== null) {
        lastCall.dt = notificationSince
      }
      const data = res.data.map((al: any) => new NotificationModel(al))
      await this.notifications.bulkPut(data)
    }
    await this.singleton.put(lastCall)
  }

  async bulkUpdateModelArticlesFromAPI(catalog: CatalogDetails, linkedCatalogs: Record<number, CatalogDetails>, articles: ModelArticleModel[], myAttributes: Record<string, IMyAttribute>) {
    await this.transaction('rw', this.articles, async () => {
      const dbArticles = await this.articles.where('[CatalogCode+Id]').anyOf(articles.map(art => [art.CatalogCode, art.Id])).toArray()
      const dbArticlesByKey: Record<string, Article> = dbArticles.reduce((acu, art) => (acu[`${art.CatalogCode}-${art.Id}`] = art) && acu, {})
      const articleData = articles.map((itm: any) => {
        const dbArticle = dbArticlesByKey[`${itm.CatalogCode}-${itm.Id}`]
        const catalogDetails = linkedCatalogs[itm.CatalogCode] || catalog
        if (utils.isDefined(dbArticle)) {
          if (catalogDetails.DataSourceTypeId === 3) {
            itm.Status = itm.PS === 0 ? 3 : (itm.PS === 1 && itm.Status === 0) ? 2 : itm.Status
          }
          itm.LocalUpdatedDate = new Date()
          assign(dbArticle, itm)
          return dbArticle
        }
        const datasetAttributes = Object.values(myAttributes!).filter(attr => attr.IsDatasetAttribute)
        return new Article(catalogDetails.CatalogCode, catalogDetails.AssignedCatalogAttributes, itm, catalogDetails.DataSourceTypeId, datasetAttributes)
      })
      await this.articles.bulkPut(articleData)
    })
  }

  async bulkUpdateArticles<T extends keyof ArticleEntitiesParams>(catalogCode: number, entity: T, data: ArticleEntitiesParams[T], firstTime: boolean) {
    await this.transaction('rw', this.articles, async () => {
      switch (entity) {
        case 'deliveryDates':
          for (const itm of data as ArticleDeliverDate[]) {
            await this.articles.update([catalogCode, itm.Id], { LocalUpdatedDate: new Date(), _DeliveryDates: itm.Crds })
          }
          break
        case 'prices':
        {
          let dbArticlesById: Record<number, Article> = {}

          if (!firstTime) {
            const dbArticles = await this.articles.where('[CatalogCode+Id]').anyOf(data.map((itm: any) => [catalogCode, itm.Id])).toArray()
            dbArticlesById = dbArticles.reduce((acu, art) => (acu[art.Id] = art) && acu, {})
          }

          for (const itm of data as ArticlePrice[]) {
            const prices = utils.isDefined(dbArticlesById[itm.Id]) ? dbArticlesById[itm.Id]._Prices : {}
            assign(prices, utils.toRecord(itm.Prices, 'PriceGroupId'))
            await this.articles.update([catalogCode, itm.Id], { LocalUpdatedDate: new Date(), _Prices: prices })
          }

          break
        }
        case 'segmentations':
        {
          let dbArticlesById: Record<number, Article> = {}

          if (!firstTime) {
            const dbArticles = await this.articles.where('[CatalogCode+Id]').anyOf(data.map((itm: any) => [catalogCode, itm.Id])).toArray()
            dbArticlesById = dbArticles.reduce((acu, art) => (acu[art.Id] = art) && acu, {})
          }

          for (const itm of data as ArticleSegmentation[]) {
            let segs = {}

            if (!firstTime && utils.isDefined(dbArticlesById[itm.Id])) {
              // If new segmentations exist in the response, reset existing ones and add the new ones
              if (itm.Segmentations && Object.keys(itm.Segmentations).length > 0) {
                segs = utils.toRecord(itm.Segmentations, 'Id')
              }
            }
            else {
              // If it's the first time or no existing segmentations, assign the new segmentations
              if (itm.Segmentations && Object.keys(itm.Segmentations).length > 0) {
                segs = utils.toRecord(itm.Segmentations, 'Id')
              }
            }

            // Update the article in the database with the new or empty segmentations
            await this.articles.update([catalogCode, itm.Id], { LocalUpdatedDate: new Date(), _Segmentations: segs })
          }
          break
        }
        case 'sizes':
          for (const itm of data as ArticleSize[]) {
            await this.articles.update([catalogCode, itm.Id], { LocalUpdatedDate: new Date(), _Sizes: itm.Sizes })
          }
          break
        default:
          console.warn('Attempting to update invalid entity of articles', entity)
          break
      }
    })
  }

  async getCatalogsList(sortBy?: string, desc?: boolean) {
    // Try to call API
    try {
      const res = await Net.t1.get<ICatalog[]>('/catalogs')
      await this.catalogs.clear()
      await this.catalogs.bulkPut(res.data)
      await this.catalogs.where('Status').equals(0).delete() // Delete inactive catalogs
    }
    catch (error) {
      console.warn('Unable to fetch catalogs list', error)
    }
    const catalogCollection = this.catalogs.where('Visibility').anyOf([1, 3])
    if (sortBy) {
      if (desc) {
        return await catalogCollection.reverse().sortBy(sortBy)
      }
      else {
        return await catalogCollection.sortBy(sortBy)
      }
    }
    else {
      return await catalogCollection.toArray()
    }
  }

  async getCatalog(catalogCode: number) {
    return await this.catalogs.where({ CatalogCode: catalogCode }).first()
  }

  async getLinkedCustomers(catalog: CatalogDetails, fetchLive: boolean = false, sortBy?: string) {
    try {
      if (fetchLive) {
        const res = await Net.t1.get(`/catalogs/${catalog.CatalogCode}/customers?status=1`)
        const data = res.data.map((lc: any) => new LinkedCustomer(catalog.CatalogCode, lc))
        await this.linkedCustomers.clear()
        await this.linkedCustomers.bulkPut(data)
      }
    }
    catch (e) {
      if (isAxiosError(e) && e.response && e.response.status === 403) {
        await this.linkedCustomers.delete(catalog.CatalogCode)
      }
      console.warn('Unable to fetch linked customers from API', e)
    }
    if (sortBy) {
      return await this.linkedCustomers.where({ CatalogCode: catalog.CatalogCode }).sortBy(sortBy)
    }
    else {
      return await this.linkedCustomers.where({ CatalogCode: catalog.CatalogCode }).toArray()
    }
  }

  async getCustomerSegmentations(catalog: CatalogDetails, customerId: number, updateDBData = true) {
    if (updateDBData) {
      try {
        await this.loadEntitySinceLastCall('CustomerSegmentations', catalog)
      }
      catch (error) {
        console.warn('Unable to fetch linked customers from API', error)
      }
    }
    return await this.customerSegmentations.where({ CatalogCode: catalog.CatalogCode, Id: customerId }).first()
  }

  async getCatalogDetails(catalogCode: number, fetchLive = true) {
    try {
      if (fetchLive) {
        const res = await getCatalogDetails(catalogCode)
        // removing since we already has vetting list in catalog details
        // if (res.data.Status > 0) {
        //   res.data.AttributeVettingList = await Net.t1.get(`/catalogs/${catalogCode}/attributevalues`)
        // }
        await this.catalogDetails.put(new CatalogDetails(res.data, []))
      }
    }
    catch (e) {
      if (isAxiosError(e) && e.response && e.response.status === 403) {
        await this.catalogDetails.delete(catalogCode)
      }
      console.warn('Unable to fetch catalog details from API', e)
    }
    return await this.catalogDetails.get(catalogCode) as CatalogDetails
  }

  async getSellerDetails(accountId: number) {
    try {
      const res = await Net.t1.get(`/sellers/${accountId}?status=0`)
      await this.sellerDetails.put(new SellerDetails(res.data))
    }
    catch (e) {
      if (isAxiosError(e) && e.response && e.response.status === 403) {
        await this.sellerDetails.delete(accountId)
      }
      console.warn('Unable to fetch catalog details from API', e)
    }
    return await this.sellerDetails.get(accountId) as SellerDetails
  }

  async getCatalogTreeDefinition(catalogCode: number) {
    try {
      const res = await Net.t1.get(`/catalogs/${catalogCode}/trees`)
      const data = res.data.map((itm: any) => new CatalogTreeDef(catalogCode, itm))
      await this.catalogTreeDef.bulkPut(data)
      await this.catalogTreeDef.where('Status').equals(0).delete()
    }
    catch (error) {
      console.warn('Unable to get catalog tree definition', error)
    }
    return await this.catalogTreeDef.where({ CatalogCode: catalogCode }).toArray()
  }

  async getLinkedCustomer(catalogCode: number, customerId: number) {
    const customer = await this.linkedCustomers.get([catalogCode, customerId])
    try {
      if (utils.isDefined(customer)) {
        const res = await getCustomerAllocationGroups(catalogCode, customerId)
        customer.AllocationGroups = res.data.AllocationGroups.filter(x => x.Status > 0).map(itm => new LinkedCustomerAllocationGroup(itm))
        await this.linkedCustomers.put(customer)
      }
    }
    catch (error) {
      console.warn('Unable to fetch customer allocations from API', error)
    }
    return customer
  }

  async getCurrentCustomerSegmentations(sellerId: number, catalogCode: number, customerId: number) {
    try {
      const res = await Net.t1.get(`/sellers/${sellerId}/catalogs/${catalogCode}/customers/${customerId}/customersegmentations`)
      return new CustomerSegmentation(catalogCode, customerId, res.data.filter(segmentation => segmentation.Status === 1))
    }
    catch (error) {
      console.warn('Unable to get customer segmentations', error)
    }
  }

  async getArticleDropReasons(sellerId: number, status?: number) {
    try {
      const res = await getArticleDropReasons(sellerId)
      const data = res.data.map(itm => new DropReason(sellerId, itm))
      await this.dropReasons.bulkPut(data)
    }
    catch (error) {
      console.warn('Unable to get article drop reasons', error)
    }
    const query = this.dropReasons.where({ SellerAccountId: sellerId })
    if (utils.isDefined(status)) {
      query.and(x => x.Status === status)
    }
    return await query.toArray()
  }

  // async buildFlatTree(catalogCode: number, id: number): Promise<ITreeNode[]> {
  //   const treeDef = await this.catalogTreeDef.get([catalogCode, id])
  //   if (!treeDef) throw new Error('Tree Def not found!')
  //   const nodes: ITreeNode[] = []
  //   const nodeId: any = {}
  //   let parentId = ''
  //   await this.articles.where({ CatalogCode: catalogCode, Status: 1 }).each(art => {
  //     parentId = ''
  //     for (let index = 0; index < treeDef.Attributes.length; index++) {
  //       const att = treeDef.Attributes[index]
  //       if (art[att] == null || art[att] === '') {
  //         break
  //       }
  //       const id = parentId + '_' + art[att]
  //       if (!Object.prototype.hasOwnProperty.call(nodeId, id)) {
  //         nodes.push({ id: nodes.length + 1, text: art[att]?.toString() || '', parentId: parentId === '' ? undefined : nodeId[parentId] + 1, arts: [] })
  //         nodeId[id] = nodes.length - 1
  //       }
  //       nodes[nodeId[id]].arts.push(art.Id)
  //       parentId = id
  //     }
  //   })
  //   return nodes
  // }

  async buildTree(catalog: CatalogDetails, id: number, customerId: number, customerSegmentations: undefined | CustomerSegmentation, myAttributes?: Record<string, IMyAttribute>, ActiveArticlesOnly: boolean = true): Promise<ITreeNode[]> {
    const treeDef = await this.catalogTreeDef.get([catalog.CatalogCode, id])
    if (!treeDef) { throw new Error('Tree Def not found!') }
    const filter = { CatalogCode: catalog.CatalogCode } as { [key: string]: any }
    if (ActiveArticlesOnly) { filter.Status = 1 }

    const articleNodes = await getCatalogArticlesTree(catalog.CatalogCode, id, customerId)

    function transformToTreeNode(apiNodes: GetCatalogArticlesTreeModel[]): ITreeNode[] {
      return apiNodes.map(node => ({
        label: utils.isValidStringValue(node.Label) ? node.Label : '(Blank)',
        key: node.Key,
        path: node.Path.map(x => ({ a: x.A, v: x.V })),
        checked: false,
        expanded: false,
        sortOrder: node.SortOrder,
        children: transformToTreeNode(node.Children || []),
        actions: [],
      }))
    }

    const nodes = transformToTreeNode(articleNodes)

    if (catalog.RequestsEnabled > 0) {
      filter.IsCreateArticleRequest = 1
      const requests = await this.requests.where(filter).toArray()
      for (const request of requests) {
        if (request.Content) {
          const requestArticle = await utils.getRequestArticle(catalog, request, this, myAttributes)
          this.addArticleToTreeDef(treeDef, requestArticle, nodes)
        }
      }
    }

    // Sort Tree
    // For now we will sort alphabetically
    const sortTree = function (nodes: ITreeNode[]) {
      nodes.sort((a, b) => {
        if (a.label === BLANK_TREE_NODE_LABEL) { return 1 }
        if (b.label === BLANK_TREE_NODE_LABEL) { return -1 }

        const la = a.label.toLowerCase()
        const lb = b.label.toLowerCase()
        if (la < lb) { return -1 }
        if (la > lb) { return 1 }
        return 0
      })
      nodes.forEach((child) => {
        if (child.children && child.children.length > 0) {
          sortTree(child.children)
        }
      })
    }
    sortTree(nodes)
    return nodes
  }

  addArticleToTreeDef(treeDef: CatalogTreeDef, art: Article, nodes: ITreeNode[]) {
    let currentNode = undefined as ITreeNode | undefined
    let parentNode = nodes
    const path = [] as { a: string, v?: string }[]
    let pathStr = ''

    for (const att of treeDef.Attributes) {
      const attVals: { key?: string, label: string }[] = []
      if (utils.isDefined(art[att])) {
        if (Array.isArray(art[att])) {
          const multiValues = art[att] as string[]
          multiValues.forEach(v => attVals.push({ key: v, label: v }))
        }
        else {
          const value = art[att]!.toString()
          attVals.push({ key: value, label: value })
        }
      }
      if (attVals.length === 0) {
        attVals.push({ key: undefined, label: BLANK_TREE_NODE_LABEL })
      }
      attVals.forEach((itm) => {
        currentNode = parentNode.find(n => n.label === itm.label)
        path.push({ a: att, v: itm.key })
        pathStr += `\\${itm.key}`
        if (!currentNode) {
          currentNode = {
            label: itm.label,
            key: utils.hashCode(pathStr),
            path: [...path],
            checked: false,
            expanded: false,
            sortOrder: 0,
            children: [] as ITreeNode[],
            actions: [],
          }
          parentNode.push(currentNode)
        }

        parentNode = currentNode.children
      })
    }
  }

  convertToArticles(catalog: CatalogDetails, indexedLinkedCatalogDetails: Record<string, CatalogDetails>, responseArticles: ArticleModel[], customerSegmentations?: undefined | CustomerSegmentation, returnNonSegmentedArticles: boolean = false, myAttributes?: Record<string, IMyAttribute>, indexedFavoriteTags: Record<string, FavoriteTag[]> = {}, considerArticleCatalogCode = false) {
    const articles: Article[] = []
    if (responseArticles && responseArticles.length) {
      responseArticles.forEach((record: any) => {
        const catalogDetails = indexedLinkedCatalogDetails[record.CatalogCode] || catalog
        const datasetAttributes = myAttributes ? Object.values(myAttributes!).filter(attr => attr.IsDatasetAttribute) : []
        const article = new Article(considerArticleCatalogCode ? record.CatalogCode : catalogDetails.CatalogCode, catalogDetails.AssignedCatalogAttributes, record, catalogDetails.DataSourceTypeId, datasetAttributes)
        if (customerSegmentations && returnNonSegmentedArticles) {
          article._IsNonSegmented = !this.isArticleSegmented(article._Segmentations, customerSegmentations.Segmentations, catalog._IndexedCatalogSegmentation)
        }
        const favoriteTagKey = `${article.CatalogCode}//${article.Id}`
        article._FavoriteTags = utils.isDefined(indexedFavoriteTags[favoriteTagKey]) ? indexedFavoriteTags[favoriteTagKey].map(itm => new SimpleFavoriteTag(itm.Id, itm.Tag, itm.Color, itm.CreatedByUserName, itm.CustomerId, itm.SharedUsers, itm.SharedUsersGroups)) : []
        articles.push(article)
      })
    }
    return articles
  }

  async convertToMyArticles(catalog: CatalogDetails, indexedLinkedCatalogDetails: Record<string, CatalogDetails>, myAttributes: Record<string, IMyAttribute>, myUsername: string, myUserId: number, responseArticles: ArticleModel[], currentCustomer: LinkedCustomer | undefined, retailPg?: CatalogPriceGroup, wholesalePg?: CatalogPriceGroup, outletPg?: CatalogPriceGroup, customerSegmentations?: undefined | CustomerSegmentation, returnNonSegmentedArticles: boolean = false) {
    const articles = this.convertToArticles(catalog, indexedLinkedCatalogDetails, responseArticles, customerSegmentations, returnNonSegmentedArticles, myAttributes)
    return await this.buildMyArticles(articles, catalog, utils.getIndexedLinkedCatalogPriceGroupByLowercaseName(indexedLinkedCatalogDetails), myAttributes, myUsername, myUserId, currentCustomer, retailPg, wholesalePg, outletPg)
  }

  /**
   * @description query and return sorted list of MyArticles based on catalog and articleIds passed to this method
   * @param { CatalogDetails } catalog CatalogDetails from which the articles will be fetched
   * @param { Record<string, CatalogDetails> } indexedLinkedCatalogDetails indexed linked catalogs by catalog code
   * @param { Record<string, IMyAttribute> } myAttributes indexed IMyAttribute
   * @param { string } myUsername
   * @param { number } myUserId
   * @param { Array<number> } articleIds list of article ids to query
   * @param { CatalogPriceGroup } retailPg selected retail price group from active catalog
   * @param { CatalogPriceGroup } wholesalePg selected wholesale price group from active catalog
   * @param { CatalogPriceGroup } outletPg selected outlet price group from active catalog
   * @param { Array<number> } linkedCatalogCodes
   * @param { LinkedCustomer | undefined } currentCustomer
   * @param { CustomerSegmentation | undefined } customerSegmentations
   * @param { boolean } returnNonSegmentedArticles (default false)
   * @returns { Promise<Array<MyArticle>> } promise which resolve to list of sorted MyArticles
   */
  async getMyArticlesByIds(catalog: CatalogDetails, indexedLinkedCatalogDetails: Record<string, CatalogDetails>, myAttributes: Record<string, IMyAttribute>, myUsername: string, myUserId: number, articleIds: number[], currentCustomer: LinkedCustomer | undefined, retailPg?: CatalogPriceGroup, wholesalePg?: CatalogPriceGroup, outletPg?: CatalogPriceGroup, linkedCatalogCodes?: number[] | undefined, customerSegmentations?: undefined | CustomerSegmentation, returnNonSegmentedArticles: boolean = false, signal: AbortSignal | undefined = undefined) {
    const articles = await this.getArticlesByCatalogCodeArticleIds(catalog, indexedLinkedCatalogDetails, articleIds, currentCustomer, myAttributes, linkedCatalogCodes, true, customerSegmentations, returnNonSegmentedArticles, undefined, signal)
    const myArticles = await this.buildMyArticles(articles, catalog, utils.getIndexedLinkedCatalogPriceGroupByLowercaseName(indexedLinkedCatalogDetails), myAttributes, myUsername, myUserId, currentCustomer, retailPg, wholesalePg, outletPg)
    return this.sortArticles(myArticles)
  }

  async getMyArticlesByIdsForMultipleSeasons(catalog: CatalogDetails, indexedLinkedCatalogPriceGroupByLowercaseName: Record<number, Record<string, CatalogPriceGroup>>, myAttributes: Record<string, IMyAttribute>, myUsername: string, myUserId: number, articleIds: number[], currentCustomer: LinkedCustomer | undefined, retailPg?: CatalogPriceGroup, wholesalePg?: CatalogPriceGroup, outletPg?: CatalogPriceGroup, linkedCatalogCodes?: number[] | undefined, customerSegmentations?: undefined | CustomerSegmentation, returnNonSegmentedArticles: boolean = false, signal: AbortSignal | undefined = undefined) {
    const articles = await this.getArticlesByCatalogCodeArticleIds(catalog, {}, articleIds, currentCustomer, myAttributes, linkedCatalogCodes, true, customerSegmentations, returnNonSegmentedArticles, undefined, signal, true)
    const myArticles = await this.buildMyArticles(articles, catalog, indexedLinkedCatalogPriceGroupByLowercaseName, myAttributes, myUsername, myUserId, currentCustomer, retailPg, wholesalePg, outletPg)
    return this.sortArticles(myArticles)
  }

  async getMyArticlesByModelIds(catalog: CatalogDetails, indexedLinkedCatalogDetails: Record<string, CatalogDetails>, myAttributes: Record<string, IMyAttribute>, myUsername: string, myUserId: number, modelIds: number[], currentCustomer: LinkedCustomer | undefined, retailPg?: CatalogPriceGroup, wholesalePg?: CatalogPriceGroup, outletPg?: CatalogPriceGroup, linkedCatalogCodes?: number[] | undefined, includeInactiveArticles: boolean = true, customerSegmentations?: undefined | CustomerSegmentation, returnNonSegmentedArticles: boolean = false) {
    const articles = await this.getArticlesByCatalogCodeModelIds(catalog, indexedLinkedCatalogDetails, modelIds, currentCustomer, linkedCatalogCodes, includeInactiveArticles, customerSegmentations, returnNonSegmentedArticles)
    const myArticles = await this.buildMyArticles(articles, catalog, utils.getIndexedLinkedCatalogPriceGroupByLowercaseName(indexedLinkedCatalogDetails), myAttributes, myUsername, myUserId, currentCustomer, retailPg, wholesalePg, outletPg)
    return this.sortArticles(myArticles)
  }

  async getRequestArticleById(catalog: CatalogDetails, requestId: number, retailPg?: CatalogPriceGroup, wholesalePg?: CatalogPriceGroup, outletPg?: CatalogPriceGroup) {
    const request = await this.requests.where({ CatalogCode: catalog.CatalogCode, Id: requestId }).first()
    if (request && request.Content) {
      const requestArticle = await utils.getRequestArticle(catalog, request, this)
      const retailPrice = retailPg ? requestArticle._Prices[retailPg.Id]?.Price : 0
      const wholesalePrice = wholesalePg ? requestArticle._Prices[wholesalePg.Id]?.Price : 0
      const outletPrice = outletPg ? requestArticle._Prices[outletPg.Id]?.Price : 0
      return new MyArticle(requestArticle, catalog.AssignedCatalogAttributes, retailPrice, wholesalePrice, outletPrice, [], true, [], [], [], catalog.Season, catalog.DataSourceTypeId, catalog!.Config.ArticlesCustomSortOrder, [])
    }
    return undefined
  }

  /**
   * @description query and return list of MyArticles based on catalog and articleNumber passed to this method
   * @param { CatalogDetails } catalog CatalogDetails from which the article will be fetched
   * @param { Record<string, CatalogDetails> } indexedLinkedCatalogDetails indexed linked catalogs by catalog code
   * @param { Record<string, IMyAttribute> } myAttributes indexed IMyAttribute
   * @param { string } myUsername
   * @param { number } myUserId
   * @param { string } articleNumbers article number to query
   * @param { CatalogPriceGroup } retailPg selected retail price group from active catalog
   * @param { CatalogPriceGroup } wholesalePg selected wholesale price group from active catalog
   * @param { CatalogPriceGroup } outletPg selected outlet price group from active catalog
   * @param { LinkedCustomer } currentCustomer current customer
   * @param { CustomerSegmentation | undefined } customerSegmentations
   * @returns { Promise<Array<MyArticle>> } promise which resolve to list of MyArticles
   */
  async getMyArticlesByArticleNumbers(catalog: CatalogDetails, indexedLinkedCatalogDetails: Record<string, CatalogDetails>, myAttributes: Record<string, IMyAttribute>, myUsername: string, myUserId: number, articleNumbers: string[], currentCustomer: LinkedCustomer | undefined, retailPg?: CatalogPriceGroup, wholesalePg?: CatalogPriceGroup, outletPg?: CatalogPriceGroup, customerSegmentations?: undefined | CustomerSegmentation) {
    const articles = await this.getArticlesByCatalogCodeArticleNumbers(catalog, indexedLinkedCatalogDetails, articleNumbers, currentCustomer, [], true, customerSegmentations, myAttributes)
    return await this.buildMyArticles(articles, catalog, utils.getIndexedLinkedCatalogPriceGroupByLowercaseName(indexedLinkedCatalogDetails), myAttributes, myUsername, myUserId, currentCustomer, retailPg, wholesalePg, outletPg)
  }

  /**
   * @description query and return active articles that represent the model
   * @param { CatalogDetails } catalog CatalogDetails from which the article will be fetched
   * @param { Record<string, CatalogDetails> } indexedLinkedCatalogDetails indexed linked catalogs by catalog code
   * @param { Record<string, IMyAttribute> } myAttributes indexed IMyAttribute
   * @param { string } myUsername
   * @param { number } myUserId
   * @param { Array<[string]> } modelNumbers model number array to query
   * @param { CatalogPriceGroup } retailPg selected retail price group from active catalog
   * @param { CatalogPriceGroup } wholesalePg selected wholesale price group from active catalog
   * @param { CatalogPriceGroup } outletPg selected outlet price group from active catalog
   * @param { LinkedCustomer } currentCustomer current customer
   * @param { CustomerSegmentation | undefined } customerSegmentations
   * @returns { Promise<Array<MyArticle>> } promise which resolve to list of MyArticles
   */
  async getMyArticlesByModelNumbers(catalog: CatalogDetails, indexedLinkedCatalogDetails: Record<string, CatalogDetails>, myAttributes: Record<string, IMyAttribute>, myUsername: string, myUserId: number, modelNumbers: string[], currentCustomer: LinkedCustomer | undefined, retailPg?: CatalogPriceGroup, wholesalePg?: CatalogPriceGroup, outletPg?: CatalogPriceGroup, customerSegmentations?: undefined | CustomerSegmentation) {
    const articles = await this.getArticlesByCatalogCodeStatusModelNumbers(catalog, indexedLinkedCatalogDetails, modelNumbers, currentCustomer, [], false, customerSegmentations)
    return await this.buildMyArticles(articles, catalog, utils.getIndexedLinkedCatalogPriceGroupByLowercaseName(indexedLinkedCatalogDetails), myAttributes, myUsername, myUserId, currentCustomer, retailPg, wholesalePg, outletPg)
  }

  /**
   * @description query and return article that represent the model
   * @param { Array<[string]> } modelNumbers model number array to query
   * @param { CatalogDetails } catalog CatalogDetails from which the article will be fetched
   * @param { Record<string, CatalogDetails> } indexedLinkedCatalogDetails indexed linked catalogs by catalog code
   * @param { Record<string, IMyAttribute> } myAttributes indexed IMyAttribute
   * @param { string } myUsername
   * @param { CatalogPriceGroup } retailPg selected retail price group from active catalog
   * @param { CatalogPriceGroup } wholesalePg selected wholesale price group from active catalog
   * @param { CatalogPriceGroup } outletPg selected outlet price group from active catalog
   * @returns { Promise<Array<MyArticle>> } promise which resolve to list of MyArticles
   */
  async getFirstActiveMyArticleByModelNumbers(modelNumbers: string[], catalog: CatalogDetails, indexedLinkedCatalogDetails: Record<string, CatalogDetails>, myAttributes: Record<string, IMyAttribute>, myUsername: string, myUserId: number, currentCustomer: LinkedCustomer | undefined, retailPg?: CatalogPriceGroup, wholesalePg?: CatalogPriceGroup, outletPg?: CatalogPriceGroup, linkedCatalogCodes?: number[] | undefined, includeInactiveArticles: boolean = false, signal?: AbortSignal) {
    const articles: Article[] = []

    const filters: ArticleFilterModel[] = []
    if (modelNumbers.length === 1) {
      filters.push({ attribute: 'ModelNumber', mode: FilterCriteriaMode.exactString, value: modelNumbers[0], exclude: false })
    }
    else {
      filters.push({ attribute: 'ModelNumber', mode: FilterCriteriaMode.multiString, value: modelNumbers, exclude: false })
    }
    if (linkedCatalogCodes?.length) {
      filters.push({
        attribute: '_Seasons',
        mode: FilterCriteriaMode.multiString,
        value: linkedCatalogCodes,
        exclude: false,
      })
    }
    const status = includeInactiveArticles ? undefined : true
    const responseArticles = await getArticlesStream(catalog.CatalogCode, { status, firstArticle: true, body: { filters } }, signal)
    if (responseArticles && responseArticles.length) {
      responseArticles.forEach((record: any) => {
        const datasetAttributes = Object.values(myAttributes).filter(attr => attr.IsDatasetAttribute)
        const catalogDetails = indexedLinkedCatalogDetails[record.CatalogCode] || catalog
        articles.push(new Article(catalogDetails.CatalogCode, catalogDetails.AssignedCatalogAttributes, record, catalogDetails.DataSourceTypeId, datasetAttributes))
      })
    }

    return await this.buildMyArticles(articles.length ? articles : [], catalog, utils.getIndexedLinkedCatalogPriceGroupByLowercaseName(indexedLinkedCatalogDetails), myAttributes, myUsername, myUserId, currentCustomer, retailPg, wholesalePg, outletPg)
  }

  async getFirstActiveMyArticleByModelIds(modelIds: number[], catalog: CatalogDetails, indexedLinkedCatalogDetails: Record<string, CatalogDetails>, myAttributes: Record<string, IMyAttribute>, myUsername: string, myUserId: number, currentCustomer: LinkedCustomer | undefined, retailPg?: CatalogPriceGroup, wholesalePg?: CatalogPriceGroup, outletPg?: CatalogPriceGroup, linkedCatalogCodes?: number[] | undefined, includeInactiveArticles: boolean = false, signal?: AbortSignal) {
    const articles: Article[] = []

    const filters: ArticleFilterModel[] = []
    if (modelIds.length === 1) {
      filters.push({ attribute: 'ModelId', mode: FilterCriteriaMode.exactString, value: modelIds[0], exclude: false })
    }
    else {
      filters.push({ attribute: 'ModelId', mode: FilterCriteriaMode.multiString, value: modelIds, exclude: false })
    }
    if (linkedCatalogCodes?.length) {
      filters.push({
        attribute: '_Seasons',
        mode: FilterCriteriaMode.multiString,
        value: linkedCatalogCodes,
        exclude: false,
      })
    }
    const status = includeInactiveArticles ? undefined : true

    const responseArticles = await getArticlesStream(catalog.CatalogCode, { status, firstArticle: true, body: { filters } }, signal)
    if (responseArticles && responseArticles.length) {
      responseArticles.forEach((record: any) => {
        const datasetAttributes = Object.values(myAttributes).filter(attr => attr.IsDatasetAttribute)
        const catalogDetails = indexedLinkedCatalogDetails[record.CatalogCode] || catalog
        articles.push(new Article(catalogDetails.CatalogCode, catalogDetails.AssignedCatalogAttributes, record, catalogDetails.DataSourceTypeId, datasetAttributes))
      })
    }

    return await this.buildMyArticles(articles.length ? articles : [], catalog, utils.getIndexedLinkedCatalogPriceGroupByLowercaseName(indexedLinkedCatalogDetails), myAttributes, myUsername, myUserId, currentCustomer, retailPg, wholesalePg, outletPg)
  }

  /**
   * @description a method that return count of active article for a model based on catalog code and modelNumber passed to this method
   * @param modelNumber model number to query
   * @param catalogCode catalog code of catalog from which the article will be fetched
   * @returns { Promise<number> } promise which resolve to a number
   */
  async getModelSKUCount(modelNumber: string, catalogCode: number, signal?: AbortSignal) {
    const filters: ArticleFilterModel[] = [
      { attribute: 'ModelNumber', mode: FilterCriteriaMode.exactString, value: modelNumber, exclude: false },
    ]
    return await getArticlesCount(catalogCode, { status: true, body: { filters } }, signal)
  }

  async getRequestArticlesByCriteria(catalog: CatalogDetails, myAttributes: Record<string, IMyAttribute>, criteria: FilterCriteria[], matchAll: boolean) {
    const requestArticles: Article[] = []

    let statusIndex = -1
    for (let i = 0; i < criteria.length; i++) {
      if (criteria[i].attribute === 'Status') {
        statusIndex = i
      }
    }
    const statusVal = statusIndex >= 0 && criteria[statusIndex]?.statusVal != null ? criteria[statusIndex]?.statusVal as number : 1

    const requests = await (statusVal === -1
      ? this.requests.where({ CatalogCode: catalog.CatalogCode, IsCreateArticleRequest: 1 })
      : this.requests.where({ CatalogCode: catalog.CatalogCode, Status: statusVal, IsCreateArticleRequest: 1 })
    ).toArray()

    for (const request of requests) {
      if (requestArticles.length >= REQUESTS_LIMIT) {
        break
      }

      if (request.Content) {
        const requestArticle = await utils.getRequestArticle(catalog, request, this, myAttributes)
        if (this.articleMatchesCriteria(requestArticle, myAttributes, criteria, matchAll, true)) {
          requestArticles.push(requestArticle)
        }
      }
    }

    return requestArticles
  }

  async getArticlesByCriteria(catalog: CatalogDetails, myAttributes: Record<string, IMyAttribute>, criteria: FilterCriteria[], matchAll: boolean, userId: number, userName: string, priceGroups: { retail?: CatalogPriceGroup, wholesale?: CatalogPriceGroup, outlet?: CatalogPriceGroup }, currentCustomer?: LinkedCustomer, customerSegmentations?: CustomerSegmentation, matchCurrentCatalogOnlyFromCriteria = false, loadRequests = false) {
    let statusIndex = -1
    let seasonIndex = -1
    for (let i = 0; i < criteria.length; i++) {
      if (criteria[i].attribute === 'Status') {
        statusIndex = i
      }
      else if (criteria[i].attribute === '_Seasons') {
        seasonIndex = i
      }
    }
    const catalogCodes: Array<number> = []
    if (!matchCurrentCatalogOnlyFromCriteria) {
      catalogCodes.push(catalog.CatalogCode)
    }
    if (seasonIndex >= 0 && utils.isDefined(criteria[seasonIndex]) && utils.isDefined(criteria[seasonIndex].multipleVals)) {
      criteria[seasonIndex].multipleVals!.forEach((val) => {
        catalogCodes.push(Number(val))
      })
    }

    let status: boolean | undefined
    if (statusIndex === -1) {
      status = true
    }
    else if (criteria[statusIndex].value === -1) {
      criteria.splice(statusIndex, 1)
    }
    const statusVal = statusIndex >= 0 && criteria[statusIndex]?.statusVal != null ? criteria[statusIndex]?.statusVal as number : 1

    const filters = await Promise.all(criteria.map(async c => await c.toApiFilter(priceGroups)))

    if (utils.isDefined(currentCustomer) && utils.isDefined(customerSegmentations) && customerSegmentations.Segmentations.length > 0) {
      filters.unshift({
        attribute: '_CustomerSegmentations',
        mode: FilterCriteriaMode.multiString,
        value: customerSegmentations.Segmentations.filter(s => s.Status > 0).map(s => s.SegmentationId),
        exclude: false,
      })
    }

    const favoriteTags = await this.myFavorites.where('[CatalogCode+UserId+Status+CustomerId]').anyOf(catalogCodes.map(catalogCode => [catalogCode, userId, 1, currentCustomer ? currentCustomer.CustomerId : -1])).toArray()
    const indexedFavoriteTags: Record<string, FavoriteTag[]> = {}
    favoriteTags.sort((a, b) => {
      if (a.CreatedByUserName === userName) { return -1 }
      return a.Tag.localeCompare(b.Tag)
    })
    favoriteTags.forEach((favoriteTag) => {
      if (favoriteTag.Articles && favoriteTag.Articles.length) {
        favoriteTag.Articles.forEach((articleId) => {
          const key = `${favoriteTag.CatalogCode}//${articleId}`
          if (!indexedFavoriteTags.hasOwnProperty(key)) {
            indexedFavoriteTags[key] = []
          }
          indexedFavoriteTags[key].push(favoriteTag)
        })
      }
    })
    const sharedFavoriteTags = await this.sharedFavorites.where('[CatalogCode+UserId]').anyOf(catalogCodes.map(catalogCode => [catalogCode, userId])).toArray()
    sharedFavoriteTags.sort((a, b) => {
      return a.Tag.localeCompare(b.Tag)
    })
    sharedFavoriteTags.forEach((sharedFavoriteTag) => {
      if (sharedFavoriteTag.Articles && sharedFavoriteTag.Articles.length) {
        sharedFavoriteTag.Articles.forEach((articleId) => {
          const key = `${sharedFavoriteTag.CatalogCode}//${articleId}`
          if (!indexedFavoriteTags.hasOwnProperty(key)) {
            indexedFavoriteTags[key] = []
          }
          indexedFavoriteTags[key].push(sharedFavoriteTag)
        })
      }
    })

    const responseArticles = await getArticlesStream(catalog.CatalogCode, { status, body: { filters } })

    const articles = this.convertToArticles(catalog, {}, responseArticles, customerSegmentations, false, myAttributes, indexedFavoriteTags)

    if (loadRequests) {
      const allRequests = await (statusVal === -1
        ? this.requests.where({ CatalogCode: catalog.CatalogCode, IsCreateArticleRequest: 1 }).toArray()
        : this.requests.where({ CatalogCode: catalog.CatalogCode, Status: statusVal, IsCreateArticleRequest: 1 }).toArray()
      )

      const filteredRequests: any = []
      for (const request of allRequests) {
        if (request.Content) {
          const requestArticle = await utils.getRequestArticle(catalog, request, this, myAttributes)
          if (this.articleMatchesCriteria(requestArticle, myAttributes, criteria, matchAll)) {
            filteredRequests.push(requestArticle)
          }
        }
      }

      // add only the REQUESTS_LIMIT request articles
      const limitedRequests = filteredRequests.slice(0, REQUESTS_LIMIT)
      articles.push(...limitedRequests)
    }

    return articles
  }

  // this will return the article object
  async getLocalArticles(catalog: CatalogDetails, articleId: number, currentCustomer: LinkedCustomer | undefined, customerSegmentations?: undefined | CustomerSegmentation) {
    if (utils.isDefined(currentCustomer)) {
      const filteredArticles = await this.filterSegmentedArticlesOfCurrentCustomer(this.articles, catalog, customerSegmentations)
      // return the specific article by Id
      return filteredArticles.find(article => article.Id === articleId)
    }
    else {
      return await this.articles.where({ CatalogCode: catalog.CatalogCode, Id: articleId }).first()
    }
  }

  async getArticlesByCatalogCodeArticleIds(catalog: CatalogDetails, indexedLinkedCatalogDetails: Record<string, CatalogDetails>, articleIds: number[], currentCustomer: LinkedCustomer | undefined, myAttributes?: Record<string, IMyAttribute>, linkedCatalogCodes?: number[] | undefined, includeInactiveArticles: boolean = true, customerSegmentations?: undefined | CustomerSegmentation, returnNonSegmentedArticles: boolean = false, limit: number | undefined = undefined, signal: AbortSignal | undefined = undefined, considerArticleCatalogCode = false) {
    const filters: ArticleFilterModel[] = []
    let articleId: number | undefined
    if (articleIds.length === 1) {
      articleId = articleIds[0]
    }
    else {
      filters.push({ attribute: 'ArticleId', mode: FilterCriteriaMode.multiString, value: articleIds, exclude: false })
    }
    if (utils.isDefined(currentCustomer) && currentCustomer!.CustomerId !== -1 && customerSegmentations && !returnNonSegmentedArticles) {
      filters.push({
        attribute: '_CustomerSegmentations',
        mode: FilterCriteriaMode.multiString,
        value: customerSegmentations.Segmentations.filter(s => s.Status > 0).map(s => s.SegmentationId),
        exclude: false,
      })
    }
    if (linkedCatalogCodes?.length) {
      filters.push({
        attribute: '_Seasons',
        mode: FilterCriteriaMode.multiString,
        value: linkedCatalogCodes,
        exclude: false,
      })
    }
    const status = includeInactiveArticles ? undefined : true
    const responseArticles = await getArticlesStream(catalog.CatalogCode, { status, articleId, topResult: limit, body: { filters } }, signal)
    return this.convertToArticles(catalog, indexedLinkedCatalogDetails, responseArticles, customerSegmentations, returnNonSegmentedArticles, myAttributes, {}, considerArticleCatalogCode)
  }

  async getArticlesByCatalogCodeModelIds(catalog: CatalogDetails, indexedLinkedCatalogDetails: Record<string, CatalogDetails>, modelIds: number[], currentCustomer: LinkedCustomer | undefined, linkedCatalogCodes?: number[] | undefined, includeInactiveArticles: boolean = true, customerSegmentations?: undefined | CustomerSegmentation, returnNonSegmentedArticles: boolean = false, limit: number | undefined = undefined) {
    const filters: ArticleFilterModel[] = []
    let modelId: number | undefined
    if (modelIds.length === 1) {
      modelId = modelIds[0]
    }
    else {
      filters.push({ attribute: 'ModelId', mode: FilterCriteriaMode.multiString, value: modelIds, exclude: false })
    }
    if (utils.isDefined(currentCustomer) && currentCustomer!.CustomerId !== -1 && customerSegmentations && !returnNonSegmentedArticles) {
      filters.push({
        attribute: '_CustomerSegmentations',
        mode: FilterCriteriaMode.multiString,
        value: customerSegmentations.Segmentations.filter(s => s.Status > 0).map(s => s.SegmentationId),
        exclude: false,
      })
    }
    if (linkedCatalogCodes?.length) {
      filters.push({
        attribute: '_Seasons',
        mode: FilterCriteriaMode.multiString,
        value: linkedCatalogCodes,
        exclude: false,
      })
    }
    const status = includeInactiveArticles ? undefined : true
    const responseArticles = await getArticlesStream(catalog.CatalogCode, { status, modelId, topResult: limit, body: { filters } })
    return this.convertToArticles(catalog, indexedLinkedCatalogDetails, responseArticles, customerSegmentations, returnNonSegmentedArticles)
  }

  async getArticlesByCatalogCodeArticleNumbers(catalog: CatalogDetails, indexedLinkedCatalogDetails: Record<string, CatalogDetails>, articleNumbers: string[], currentCustomer: LinkedCustomer | undefined, linkedCatalogCodes: number[], includeInactiveArticles: boolean, customerSegmentations: undefined | CustomerSegmentation, myAttributes: Record<string, IMyAttribute>) {
    const filters: ArticleFilterModel[] = []
    if (articleNumbers.length === 1) {
      filters.push({ attribute: 'ArticleNumber', mode: FilterCriteriaMode.exactString, value: articleNumbers[0], exclude: false })
    }
    else {
      filters.push({ attribute: 'ArticleNumber', mode: FilterCriteriaMode.multiString, value: articleNumbers, exclude: false })
    }
    if (utils.isDefined(currentCustomer) && currentCustomer!.CustomerId !== -1 && customerSegmentations) {
      filters.push({
        attribute: '_CustomerSegmentations',
        mode: FilterCriteriaMode.multiString,
        value: customerSegmentations.Segmentations.filter(s => s.Status > 0).map(s => s.SegmentationId),
        exclude: false,
      })
    }
    if (linkedCatalogCodes.length) {
      filters.push({
        attribute: '_Seasons',
        mode: FilterCriteriaMode.multiString,
        value: linkedCatalogCodes,
        exclude: false,
      })
    }
    const status = includeInactiveArticles ? undefined : true

    const responseArticles = await getArticlesStream(catalog.CatalogCode, { status, body: { filters } })

    return this.convertToArticles(catalog, indexedLinkedCatalogDetails, responseArticles, customerSegmentations, false, myAttributes)
  }

  async getArticlesByCatalogCodeStatusModelNumbers(catalog: CatalogDetails, indexedLinkedCatalogDetails: Record<string, CatalogDetails>, modelNumbers: string[], currentCustomer: LinkedCustomer | undefined, linkedCatalogCodes?: number[] | undefined, includeInactiveArticles: boolean = true, customerSegmentations?: undefined | CustomerSegmentation) {
    const filters: ArticleFilterModel[] = []
    if (modelNumbers.length === 1) {
      filters.push({ attribute: 'ModelNumber', mode: FilterCriteriaMode.exactString, value: modelNumbers[0], exclude: false })
    }
    else {
      filters.push({ attribute: 'ModelNumber', mode: FilterCriteriaMode.multiString, value: modelNumbers, exclude: false })
    }
    if (utils.isDefined(currentCustomer) && currentCustomer!.CustomerId !== -1 && customerSegmentations) {
      filters.push({
        attribute: '_CustomerSegmentations',
        mode: FilterCriteriaMode.multiString,
        value: customerSegmentations.Segmentations.filter(s => s.Status > 0).map(s => s.SegmentationId),
        exclude: false,
      })
    }
    if (linkedCatalogCodes?.length) {
      filters.push({
        attribute: '_Seasons',
        mode: FilterCriteriaMode.multiString,
        value: linkedCatalogCodes,
        exclude: false,
      })
    }
    const status = includeInactiveArticles ? undefined : true

    const responseArticles = await getArticlesStream(catalog.CatalogCode, { status, body: { filters } })

    return this.convertToArticles(catalog, indexedLinkedCatalogDetails, responseArticles, customerSegmentations)
  }

  async getArticlesByCatalogCodeStatus(catalog: CatalogDetails, includeInactiveArticles: boolean, firstArticle?: boolean, currentCustomer?: LinkedCustomer, customerSegmentations?: CustomerSegmentation, limit?: number) {
    const filters: ArticleFilterModel[] = []
    if (utils.isDefined(currentCustomer) && currentCustomer!.CustomerId !== -1 && customerSegmentations) {
      filters.push({
        attribute: '_CustomerSegmentations',
        mode: FilterCriteriaMode.multiString,
        value: customerSegmentations.Segmentations.filter(s => s.Status > 0).map(s => s.SegmentationId),
        exclude: false,
      })
    }
    const status = includeInactiveArticles ? undefined : true
    const responseArticles = await getArticlesStream(catalog.CatalogCode, { status, firstArticle, topResult: limit, body: { filters } })
    return this.convertToArticles(catalog, {}, responseArticles)
  }

  // Helper method
  async filterSegmentedArticlesOfCurrentCustomer(articleCollection, catalog: CatalogDetails, customerSegmentations: undefined | CustomerSegmentation, returnNonSegmentedArticles: boolean = false) {
    const currentCustomerSegmentations = customerSegmentations && customerSegmentations.Segmentations && customerSegmentations.Segmentations.length ? customerSegmentations.Segmentations : []
    return articleCollection.filter((article) => {
    // Check if the article is segmented for current catalog
      let isSegmented = true
      if (article.CatalogCode === catalog.CatalogCode) {
        isSegmented = this.isArticleSegmented(
          article._Segmentations,
          currentCustomerSegmentations,
          catalog._IndexedCatalogSegmentation,
        )
      }

      // If the article is not segmented and the flag is true, return the article and set _IsNonSegmented to true
      if (!isSegmented && returnNonSegmentedArticles) {
        article._IsNonSegmented = true
        return true
      }

      return isSegmented
    })
  }

  isArticleSegmented(articleSegmentations, currentCustomerSegmentations: Segmentation[], indexedCatalogSegmentation) {
    for (const segmentationId in articleSegmentations) {
      if (
        indexedCatalogSegmentation[segmentationId] && indexedCatalogSegmentation[segmentationId].Status === 1
        && currentCustomerSegmentations.some(segmentation => segmentation.SegmentationId === Number.parseInt(segmentationId))
      ) {
        return true
      }
    }
    return false
  }

  // Removing this function, we should use liveArticles instead
  // async getMyArticlesByCriteria(catalog: CatalogDetails, myAttributes: Record<string, MyAttribute>, myUsername: string, criteria: FilterCriteria[], matchAll: boolean, retailPg?: CatalogPriceGroup, wholesalePg?: CatalogPriceGroup, outletPg?: CatalogPriceGroup) {
  //   console.time('getMyArticlesByCriteria')
  //   const arts = await this.getArticlesByCriteria(catalog, myAttributes, criteria, matchAll)

  //   console.debug('Done fetching, found', arts.length)

  //   let res = await this.buildMyArticles(arts, catalog,utils.getIndexedLinkedCatalogPriceGroupByLowercaseName(indexedLinkedCatalogDetails), myAttributes, myUsername, retailPg, wholesalePg, outletPg)
  //   console.debug('Built all articles')
  //   res = this.sortArticles(res)
  //   console.timeEnd('getMyArticlesByCriteria')
  //   return res

  // }

  /**
   * @description takes articles and assign user data to them and return list of MyArticles
   * @param  { Array<Article> } articles list of articles
   * @param { CatalogDetails } catalog catalog that the articles belongs to
   * @param { Record<number, Record<string, CatalogPriceGroup>> } indexedLinkedCatalogPriceGroupByLowercaseName indexed linked catalogs by catalog code
   * @param { Record<string, IMyAttribute> } myAttributes indexed IMyAttribute
   * @param { string } myUsername
   * @param { CatalogPriceGroup } retailPg selected retail price group from  active catalog
   * @param { CatalogPriceGroup } wholesalePg selected wholesale price group from active catalog
   * @param { CatalogPriceGroup } outletPg selected outlet price group from active catalog
   * @param { boolean } findOnlyPrices if only prices are needed in the required objects
   * @returns { Array<MyArticle> } Array of MyArticle
   */
  async buildMyArticles(articles: Article[], catalog: CatalogDetails, indexedLinkedCatalogPriceGroupByLowercaseName: Record<number, Record<string, CatalogPriceGroup>>, myAttributes: Record<string, IMyAttribute>, myUsername: string, myUserId: number, currentCustomer: LinkedCustomer | undefined, retailPg?: CatalogPriceGroup, wholesalePg?: CatalogPriceGroup, outletPg?: CatalogPriceGroup, findOnlyPrices?: boolean) {
    const res = [] as MyArticle[]

    const datasetAttributes = Object.values(myAttributes!).filter(attr => attr.IsDatasetAttribute)
    let allResources: Resource[] = []
    let allFavs: FavoriteTag[] = []
    const allAssets: DuneAsset[] = []
    let allAllocations: Allocation[] = []
    if (articles.length > 0 && !findOnlyPrices) {
      await this.transaction('r', this.myFavorites, this.sharedFavorites, this.allocations, this.resources, async () => {
        // console.debug('Loading all prices')
        // allPrices = utils.arrayToNumberDictionary(await this.prices.where('[CatalogCode+Id]').anyOf(articles.map(art => [catalog.CatalogCode, art.Id])).toArray(), 'Id')
        // console.debug('Loading all delivery dates')
        // allDeliveryDates = utils.arrayToNumberDictionary(await this.deliveryDates.where('[CatalogCode+Id]').anyOf(articles.map(art => [catalog.CatalogCode, art.Id])).toArray(), 'Id')
        // console.debug('Loading all segmentations')
        // allSegmentations = utils.arrayToNumberDictionary(await this.segmentations.where('[CatalogCode+Id]').anyOf(articles.map(art => [catalog.CatalogCode, art.Id])).toArray(), 'Id')
        // console.debug('Loading all sizes')
        // allSizes = utils.arrayToNumberDictionary(await this.sizes.where('[CatalogCode+Id]').anyOf(articles.map(art => [catalog.CatalogCode, art.Id])).toArray(), 'Id')
        console.debug('Loading all resources')
        allResources = (await this.resources.where('Articles').anyOf(articles.map(art => art.Id))
          .filter(itm => itm.CatalogCode === catalog.CatalogCode && itm.Status === 1)
          .toArray())
          .sort((a, b) => {
            const result = a.SortOrder - b.SortOrder
            if (result !== 0) {
              return result
            }
            else if (a.ResourceCategory.toString().trim().toLowerCase() + a.ResourceName.toString().trim().toLowerCase() < b.ResourceCategory.toString().trim().toLowerCase() + b.ResourceName.toString().trim().toLowerCase()) {
              return -1
            }
            else if (a.ResourceCategory.toString().trim().toLowerCase() + a.ResourceName.toString().trim().toLowerCase() > b.ResourceCategory.toString().trim().toLowerCase() + b.ResourceName.toString().trim().toLowerCase()) {
              return 1
            }
            else {
              return 0
            }
          })
        console.debug('Loading all Favs')
        const allMyFavs = await this.myFavorites.where('Articles').anyOf(articles.map(art => art.Id)).filter(itm => itm.CatalogCode === catalog.CatalogCode && itm.UserId === myUserId && itm.Status === 1 && itm.CreatedByUserName === myUsername && itm.CustomerId === (utils.isDefined(currentCustomer) ? currentCustomer.CustomerId : -1)).toArray()
        const allSharedFavorites = await this.sharedFavorites.where('Articles').anyOf(articles.map(art => art.Id)).filter(itm => itm.CatalogCode === catalog.CatalogCode && itm.UserId === myUserId).toArray()
        const uniqueFavoritesMap = new Map<number, typeof allMyFavs[0]>()
        for (const fav of [...allMyFavs, ...allSharedFavorites]) {
          if (!uniqueFavoritesMap.has(fav.Id)) {
            uniqueFavoritesMap.set(fav.Id, fav)
          }
        }
        allFavs = Array.from(uniqueFavoritesMap.values())

        console.debug('Loading all Allocations')
        allAllocations = await this.allocations.where('Articles').anyOf(articles.map(art => art.Id)).toArray()
      })
    }

    console.debug('Processing articles')
    for (const article of articles) {
      let retailPrice: Price | null = null
      let wholesalePrice: Price | null = null
      let outletPrice: Price | null = null

      // article belong to active catalog
      if (!indexedLinkedCatalogPriceGroupByLowercaseName || !indexedLinkedCatalogPriceGroupByLowercaseName.hasOwnProperty(article.CatalogCode)) {
        if (retailPg != null) {
          retailPrice = article._Prices[retailPg.Id]
        }
        if (wholesalePg != null) {
          wholesalePrice = article._Prices[wholesalePg.Id]
        }
        if (outletPg != null) {
          outletPrice = article._Prices[outletPg.Id]
        }
      }
      else { // article belongs to linked catalog
        const indexedCatalogPriceGroupByLowercaseName = indexedLinkedCatalogPriceGroupByLowercaseName[article.CatalogCode]
        if (retailPg != null) {
          const linkedPriceGroup = indexedCatalogPriceGroupByLowercaseName[retailPg.Name.toString().trim().toLowerCase()]
          if (linkedPriceGroup) {
            retailPrice = article._Prices[linkedPriceGroup.Id]
          }
        }
        if (wholesalePg != null) {
          const linkedPriceGroup = indexedCatalogPriceGroupByLowercaseName[wholesalePg.Name.toString().trim().toLowerCase()]
          if (linkedPriceGroup) {
            wholesalePrice = article._Prices[linkedPriceGroup.Id]
          }
        }
        if (outletPg != null) {
          const linkedPriceGroup = indexedCatalogPriceGroupByLowercaseName[outletPg.Name.toString().trim().toLowerCase()]
          if (linkedPriceGroup) {
            outletPrice = article._Prices[linkedPriceGroup.Id]
          }
        }
      }

      // Get Resources
      const resources = allResources.filter(r => r.Articles.includes(article.Id))
      // Get Favorites
      const myFavs = allFavs.filter(fav => fav.Articles.includes(article.Id))
      // Get assets
      const assets = allAssets.filter(itm => itm.ImageSet === article.ArticleNumber).sort((a, b) => a.SortOrder - b.SortOrder)
      // Get Allocations
      const allocations = allAllocations.filter(r => r.Articles.includes(article.Id))

      const simpleFavTags = myFavs.map(itm => new SimpleFavoriteTag(itm.Id, itm.Tag, itm.Color, itm.CreatedByUserName, itm.CustomerId))
      const newArt = new MyArticle(article, catalog.AssignedCatalogAttributes, retailPrice?.Price || 0, wholesalePrice?.Price || 0, outletPrice?.Price || 0, resources, true, assets, simpleFavTags, allocations, catalog.Season, catalog.DataSourceTypeId, catalog!.Config.ArticlesCustomSortOrder, datasetAttributes)
      res.push(newArt)
    }
    return res
  }

  /**
   * @description takes articles and assign user data to them and return list of MyArticles
   * @param  { Array<Article> } articles list of articles
   * @param { Array<CatalogAttribute> } assignedCatalogAttributes catalog that the articles belongs to
   * @param { string } catalogSeason
   * @param { number } catalogDataSourceTypeId
   * @param { Record<string, number>, orderedAttributeSystemName: string[] | undefined } catalogCOnfigSortOrder
   * @param { Record<string, number> } indexedCatalogPriceGroupByLowercaseName
   * @param { CatalogPriceGroup } retailPg selected retail price group from  active catalog
   * @param { CatalogPriceGroup } wholesalePg selected wholesale price group from active catalog
   * @param { CatalogPriceGroup } outletPg selected outlet price group from active catalog
   * @returns { Array<MyArticle> } Array of MyArticle
   */
  buildMyArticlesForLinkedCatalogArticleForDetails(articles: Article[], assignedCatalogAttributes: CatalogAttribute[], catalogSeason: string, catalogDataSourceTypeId: number, catalogCOnfigSortOrder: { sortOrder: Record<string, number>, orderedAttributeSystemName: string[] } | undefined, indexedCatalogPriceGroupByLowercaseName: Record<string, number>, retailPg?: CatalogPriceGroup, wholesalePg?: CatalogPriceGroup, outletPg?: CatalogPriceGroup) {
    const res = [] as MyArticle[]
    console.debug('Processing linked catalog articles')
    for (const article of articles) {
      let retailPrice: Price | null = null
      let wholesalePrice: Price | null = null
      let outletPrice: Price | null = null

      if (retailPg != null) {
        const linkedPriceGroup = indexedCatalogPriceGroupByLowercaseName[retailPg.Name.toString().trim().toLowerCase()]
        if (linkedPriceGroup) {
          retailPrice = article._Prices[linkedPriceGroup]
        }
      }
      if (wholesalePg != null) {
        const linkedPriceGroup = indexedCatalogPriceGroupByLowercaseName[wholesalePg.Name.toString().trim().toLowerCase()]
        if (linkedPriceGroup) {
          wholesalePrice = article._Prices[linkedPriceGroup]
        }
      }
      if (outletPg != null) {
        const linkedPriceGroup = indexedCatalogPriceGroupByLowercaseName[outletPg.Name.toString().trim().toLowerCase()]
        if (linkedPriceGroup) {
          outletPrice = article._Prices[linkedPriceGroup]
        }
      }

      const newArt = new MyArticle(article, assignedCatalogAttributes, retailPrice?.Price || 0, wholesalePrice?.Price || 0, outletPrice?.Price || 0, [], true, [], [], [], catalogSeason, catalogDataSourceTypeId, catalogCOnfigSortOrder, [])
      res.push(newArt)
    }
    return res
  }

  sortArticles(arts: MyArticle[]) {
    // Sort by sort order, then by article number

    return arts.sort((a, b) => {
      if (a.SortOrder === b.SortOrder) {
        const la = a.ArticleNumber ? a.ArticleNumber.toLowerCase() : ''
        const lb = b.ArticleNumber ? b.ArticleNumber.toLowerCase() : ''

        return la > lb ? 1 : la < lb ? -1 : 0
      }
      return a.SortOrder > b.SortOrder ? 1 : -1
    })
  }

  articleMatchesCriteria(art: Article | MyArticle, myAttributes: Record<string, IMyAttribute>, criteria: FilterCriteria[], matchAll: boolean, checkOwnProperty = false): boolean {
    if (matchAll) {
      const segCriteria = criteria.find(c => c.attribute === '_Segmentations')
      const planningCriteria = criteria.find(c => c.attribute === '_PlanningValue')
      if (segCriteria && planningCriteria) {
        segCriteria.extra = planningCriteria.multipleVals
      }
      return criteria.every(c => c.matchesRecord(art, checkOwnProperty))
    }
    else {
      return criteria.some(c => c.matchesRecord(art, checkOwnProperty))
    }
  }

  async createUpdateFavorite(catalogCode: number, userId: number, fav: FavoriteTag, customerId: number) {
    const requestObject = fav.toCreateUpdate()
    if (customerId && customerId !== -1) {
      requestObject.CustomerId = customerId
    }
    const res = await utils.tryAsync(createUpdateFavorite(catalogCode, requestObject))
    if (res.success) {
      const existingTag = await this.myFavorites.get({ CatalogCode: catalogCode, UserId: userId, Tag: fav.Tag, CustomerId: fav.CustomerId })
      const newFav = new FavoriteTag(catalogCode, userId, res.result.data)
      if (existingTag) {
        this.myFavorites.where('[CatalogCode+UserId+Tag+CustomerId]').equals([catalogCode, userId, fav.Tag, fav.CustomerId]).delete()
      }
      newFav.LocalUpdatedDate = new Date()
      await this.myFavorites.put(newFav)
    }
  }

  async deleteFavorite(catalogCode: number, favs: FavoriteTag[]) {
    const requestObject: UpdateFavoritesStatusModel[] = favs.map(f => ({ Id: f.Id, Status: 0 }))
    const res = await utils.tryAsync(updateFavoritesStatus(catalogCode, requestObject))
    if (res.success) {
      await this.myFavorites.bulkPut(favs)
    }
  }

  async updateFavoriteTag(catalogCode: number, oldTagName: string, userId: number, favId: number, updatedFavorite: any) {
    const res = await utils.tryAsync(updateFavorite(catalogCode, favId, clone(updatedFavorite)))
    if (res.success) {
      const existingTag = await this.myFavorites.get({ CatalogCode: catalogCode, UserId: userId, Tag: oldTagName, CustomerId: updatedFavorite.CustomerId })
      const newFav = new FavoriteTag(catalogCode, userId, res.result.data)
      if (existingTag) {
        this.myFavorites.where('[CatalogCode+UserId+Tag+CustomerId]').equals([catalogCode, userId, oldTagName, existingTag.CustomerId]).delete()
      }
      newFav.LocalUpdatedDate = new Date()
      await this.myFavorites.put(newFav)
    }
  }

  async buildMyAttributes(catalog: CatalogDetails, linkedCatalogDetails: Record<number, CatalogDetails>, myUsername: string, myUserId: number, ActiveArticlesOnly: boolean, customer: LinkedCustomer | undefined, customerSegmentation: CustomerSegmentation | undefined = undefined, activeArticleStateList: Array<ArticleStateModel> | [], isViewUnAssortedArticlesRestricted: boolean) {
    const attDic: Record<string, IMyAttribute> = {}
    const allowedPriceGroups = new Set<string>(catalog.Config.AllowedPriceGroups)
    for (const systemName in appConstants.staticAttributes) {
      const attribute = appConstants.staticAttributes[systemName]
      if (catalog.RequestsEnabled > 0 && attribute.IsRequest) {
        attDic[systemName] = attribute
      }
      else {
        if (allowedPriceGroups.size > 0) {
          if (systemName === '_RetailPrice' && !allowedPriceGroups.has('MSRP')) {
            continue
          }
          else if (systemName === '_WholesalePrice' && !allowedPriceGroups.has('Wholesale Price')) {
            continue
          }
          else if (systemName === '_OutletPrice' && !allowedPriceGroups.has('Outlet Price')) {
            continue
          }
        }
        attDic[systemName] = attribute
      }
    }
    // for child catalog add Not Assorted and Globally Dropped statuses
    if (catalog.DataSourceTypeId === appConstants.catalogTypes.inherited) {
      // TODO implement the restrict not assorted prvilage part
      attDic.Status.FilterLookup = new Map([
        [-1, 'All'],
        [1, 'Active'],
      ])
      // for Inherited Catalog dont show not assorted articles articles if the UI_ViewUnAssortedArticles is not assigned.
      if (!isViewUnAssortedArticlesRestricted) {
        attDic.Status.FilterLookup.set(2, 'Not Assorted')
        attDic.Status.FilterLookup.set(3, 'Globally Dropped')
      }
    }
    else {
      attDic.Status.FilterLookup = new Map([
        [-1, 'All'],
        [1, 'Active'],
        [0, 'Inactive'],
      ])
    }
    const staticAttributesWithFilterLookupInArticle = Object.values(attDic).filter(attribute => attribute.FilterLookupFromArticle)
    const lookupFromArticleAttributesData: string[] = staticAttributesWithFilterLookupInArticle.length ? staticAttributesWithFilterLookupInArticle.map(attribute => attribute.SystemName) : []
    const filter = { CatalogCode: catalog.CatalogCode } as Record<string, any>
    if (ActiveArticlesOnly) {
      filter.Status = 1
    }

    // Load all dynamic attributes
    for (const att of catalog.AssignedCatalogAttributes) {
      const newAtt: IMyAttribute = {
        AttributeType: att.AttributeTypeId,
        AttributeSource: att.AttributeSource,
        Creatable: att.Creatable,
        DisplayName: att.AttributeDisplayName,
        Editable: att.AttributeTypeId === AttributeType.Calc ? false : att.Editable, // for calc type, editable is false
        ReadOnly: att.AttributeTypeId === AttributeType.Calc, // for calc type ReadOnly = true
        AllowFiltering: true,
        FilterLookup: new Map(),
        IsRequired: att.IsRequired,
        IsSeasonless: att.IsSeasonless || att.IsSeasonlessModelAttribute,
        Overridable: att.Overridable,
        IsStatic: false,
        IsPersonal: false,
        SystemName: att.AttributeSystemName,
        ValidationExpression: att.ValidationExpression,
        ValidationMessage: att.ValidationMessage,
        VettingList: att.AllowedValues,
        // hide dataset attributes from list and details view and create seprate columns for dataset by its attributes
        Visible: att.AttributeTypeId === AttributeType.Dataset ? false : att.Visible,
        IsModelLevel: att.IsModelLevel || att.IsSeasonlessModelAttribute,
        IsRequest: false,
        Criteria: {},
        calcTypeDataType: undefined,
        VisibleInListByDefault: att.VisibleInListByDefault,
        DefaultValue: att.DefaultValue,
        DatasetId: att.DatasetId,
      }

      // create seprate attributes for the configured dataset attributes
      if (att.AttributeTypeId === AttributeType.Dataset && att.DatasetId) {
        const datasetDetails = await getDatasetDetails(catalog.AccountId, att.DatasetId)
        if (datasetDetails?.Attributes?.length) {
          newAtt._datasetAttributes = []
          const regex = /\{([^}]+)\}/g
          let match: RegExpExecArray | null
          const val = datasetDetails.DisplayValueExpression
          // eslint-disable-next-line no-cond-assign
          while ((match = regex.exec(val)) !== null) {
            newAtt._datasetAttributes.push(match[1])
          }

          for (const datasetAttr of datasetDetails.Attributes) {
            const datasetAttrField: IMyAttribute = {
              SystemName: `${att.AttributeSystemName}-${datasetAttr.AttributeSystemName}`,
              DisplayName: `${att.AttributeDisplayName}-${datasetAttr.AttributeDisplayName}`,
              AttributeType: AttributeType.Nvarchar,
              Visible: true,
              Creatable: false,
              Editable: false,
              IsRequired: false,
              IsStatic: false,
              IsPersonal: false,
              AllowFiltering: false,
              AttributeSource: '',
              Criteria: {},
              FilterLookup: new Map(),
              IsSeasonless: att.IsSeasonless || att.IsSeasonlessModelAttribute,
              IsModelLevel: att.IsModelLevel || att.IsSeasonlessModelAttribute,
              Overridable: false,
              IsRequest: false,
              ValidationExpression: '',
              ValidationMessage: '',
              IsDatasetAttribute: true,
              ReadOnly: true,
            }
            attDic[datasetAttrField.SystemName] = datasetAttrField
          }
        }
      }

      // Parsed the criteria
      try {
        if (utils.isValidStringValue(att.Criteria)) {
          newAtt.Criteria = JSON.parse(att.Criteria)
        }
      }
      catch (error) {
        console.error(`Invalid Criteria definition for ${att.AttributeSystemName} \n`, error)
      }

      // Parsed the validation expression
      try {
        if (utils.isValidStringValue(att.ValidationExpression)) {
          const parsed = JSON.parse(att.ValidationExpression)
          if (parsed.hasOwnProperty('F') && isObject(parsed.F)) {
            newAtt.parsedValidationExpression = parsed.F
          }
          if (att.AttributeTypeId === AttributeType.Calc && utils.isDefined(parsed.calculatedRegexExpression) && utils.isDefined(parsed.calculatedRegexExpression.type)) {
            newAtt.calcTypeDataType = parsed.calculatedRegexExpression.type
          }
        }
      }
      catch (error) {
        console.error(`unable to parsed validation expression for ${att.AttributeSystemName} \n`, error)
      }

      // If FilterLookup, add to array
      if (att.SupportFilterLookup) {
        if (!utils.isDefined(att.AllowedValues) || (att.AllowedValues && att.AllowedValues.length === 0)) {
          newAtt.FilterLookupFromArticle = true
          lookupFromArticleAttributesData.push(att.AttributeSystemName)
        }
        else {
          // when vetting list assigned list the vetting list
          att.AllowedValues.forEach((value) => {
            const key = value.toLowerCase()
            if (!newAtt.FilterLookup.has(null)) {
              newAtt.FilterLookup.set(null, '[Blank]')
            }
            if (value && !newAtt.FilterLookup.has(key)) {
              newAtt.FilterLookup.set(key, value)
            }
          })
        }
      }

      attDic[newAtt.SystemName] = newAtt
    }

    const attsLimitExcess = {} as Record<string, number>
    lookupFromArticleAttributesData.forEach((attribute) => {
      attDic[attribute].FilterLookup.clear()
    })
    // await this.articles.where(filter).each((art) => {
    //   // Build filter lookup lists
    //   lookupFromArticleAttributesData.forEach((la) => {
    //     if (!attDic[la].FilterLookup.has(null)) {
    //       attDic[la].FilterLookup.set(null, '[Blank]')
    //     }
    //     const attValues: string[] = []
    //     if (utils.isDefined(art[la])) {
    //       if (attDic[la].AttributeType === AttributeType.MultiValue) {
    //         attValues.push(...art[la] as string[])
    //       }
    //       else {
    //         attValues.push(art[la]!.toString())
    //       }
    //     }
    //     attValues.forEach((attVal) => {
    //       const key = attVal.toLowerCase()
    //       if (!attDic[la].FilterLookup.has(key)) {
    //         if (attDic[la].FilterLookup.size >= appConstants.limits.attributeLookupValues) {
    //           if (!attsLimitExcess.hasOwnProperty(la)) { attsLimitExcess[la] = 0 }
    //           attsLimitExcess[la]++
    //         }
    //         if (attDic[la].AttributeType === AttributeType.DateOption) {
    //           attVal = utils.formatDate(attVal)
    //         }
    //         attDic[la].FilterLookup.set(key, attVal)
    //       }
    //     })
    //   })
    // })

    if (Object.keys(attsLimitExcess).length > 0) {
      console.warn(`One or more attributes have unique values that exceed the lookup limit of ${appConstants.limits.attributeLookupValues}`, attsLimitExcess)
      for (const att in attsLimitExcess) {
        if (attsLimitExcess.hasOwnProperty(att)) {
          attDic[att].FilterLookup.clear()
        }
      }
    }

    const favoriteTagsAtt = attDic._FavoriteTags
    if (favoriteTagsAtt) {
      favoriteTagsAtt.FilterLookup.clear()
      filter.UserId = myUserId
      const filterForMyFavoritesWithCustomeId = clone(filter)
      filterForMyFavoritesWithCustomeId.CustomerId = customer ? customer.CustomerId : -1
      await this.myFavorites.where(filterForMyFavoritesWithCustomeId).each((favoriteTag) => {
        if (!favoriteTagsAtt.FilterLookup.has(favoriteTag.Id)) {
          favoriteTagsAtt.FilterLookup.set(favoriteTag.Id, new SimpleFavoriteTag(favoriteTag.Id, favoriteTag.Tag, favoriteTag.Color, favoriteTag.CreatedByUserName, favoriteTag.CustomerId, favoriteTag.SharedUsers, favoriteTag.SharedUsersGroups))
        }
      })
      await this.sharedFavorites.where(filter)
        .each((favoriteTag) => {
          if (!favoriteTagsAtt.FilterLookup.has(favoriteTag.Id)) {
            favoriteTagsAtt.FilterLookup.set(favoriteTag.Id, new SimpleFavoriteTag(favoriteTag.Id, favoriteTag.Tag, favoriteTag.Color, favoriteTag.CreatedByUserName, favoriteTag.CustomerId, favoriteTag.SharedUsers, favoriteTag.SharedUsersGroups))
          }
        })
    }

    const deliveryDateAtt = attDic._DeliveryDates
    deliveryDateAtt?.FilterLookup.clear()
    if (deliveryDateAtt && catalog.CatalogCRDList.length) {
      deliveryDateAtt.FilterLookup.set(null, '[Blank]')
      catalog.CatalogCRDList.forEach((crd) => {
        if (crd.Status > 0 && crd.Description) {
          let description = crd.Description
          if (catalog.Config.FilterDeliveryDateByMMMYYYYFormat) {
            const date = new Date(crd.CustomerRequiredDate)
            const mo = new Intl.DateTimeFormat('en', { month: 'short' }).format(date)
            const ye = new Intl.DateTimeFormat('en', { year: 'numeric' }).format(date)
            description = `${mo}-${ye}`
          }
          deliveryDateAtt.FilterLookup.set(crd.Id, description)
        }
      })
    }

    const segmentationAtt = attDic._Segmentations
    const segmentationPlanningValueAtt = attDic._PlanningValue
    segmentationAtt?.FilterLookup.clear()
    segmentationPlanningValueAtt?.FilterLookup.clear()
    if (segmentationAtt && ((!utils.isDefined(customer) && catalog.Config.AllowFullRangeCustomerToFilterBySegmentation)
      || (utils.isDefined(customer) && catalog.Config.AllowCustomerToFilterBySegmentation))) {
      segmentationAtt.AllowFiltering = true
      segmentationAtt.FilterLookup.set(null, '[Blank]')
      if (utils.isDefined(customerSegmentation)) {
        customerSegmentation.Segmentations.forEach((segmentation) => {
          if (catalog._IndexedCatalogSegmentation[segmentation.SegmentationId] && catalog._IndexedCatalogSegmentation[segmentation.SegmentationId].Status > 0 && catalog._IndexedCatalogSegmentation[segmentation.SegmentationId].Viewable && !appConstants.excludeSegmentationNames.has(catalog._IndexedCatalogSegmentation[segmentation.SegmentationId].Name.toLowerCase())) { segmentationAtt.FilterLookup.set(segmentation.SegmentationId, catalog._IndexedCatalogSegmentation[segmentation.SegmentationId].Name) }
        })
      }
      else {
        Object.values(catalog._IndexedCatalogSegmentation).forEach((segmentation) => {
          if (segmentation.Status > 0 && segmentation.Viewable && !appConstants.excludeSegmentationNames.has(segmentation.Name.toLowerCase())) { segmentationAtt.FilterLookup.set(segmentation.Id, segmentation.Name) }
        })
      }
      if (catalog.SegmentationPlanningEnable) {
        segmentationPlanningValueAtt.AllowFiltering = true
        segmentationPlanningValueAtt.FilterLookup.set(null, '[Blank]')
        segmentationPlanningValueAtt.FilterLookup.set('Assorted', catalog.Config.SegmentationPlanningValues?.Assorted || 'Assorted')
        segmentationPlanningValueAtt.FilterLookup.set('Planned', catalog.Config.SegmentationPlanningValues?.Planned || 'Planned')
      }
    }
    const articleStateAttribute = attDic.StateName
    articleStateAttribute?.FilterLookup.clear()
    if (articleStateAttribute && activeArticleStateList && activeArticleStateList.length) {
      articleStateAttribute.AllowFiltering = true
      activeArticleStateList.forEach((state) => {
        if (state.Status) {
          articleStateAttribute.FilterLookup.set(state.StateName, state.StateName)
        }
      })
    }
    const modelStateAttribute = attDic.ModelStateName
    modelStateAttribute?.FilterLookup.clear()
    if (modelStateAttribute && activeArticleStateList && activeArticleStateList.length) {
      modelStateAttribute.AllowFiltering = true
      activeArticleStateList.forEach((state) => {
        if (state.Status) {
          modelStateAttribute.FilterLookup.set(state.StateName, state.StateName)
        }
      })
    }
    const periodAtt = attDic.Period
    periodAtt?.FilterLookup.clear()
    if (periodAtt && catalog.ShipmentWindowRangeList.length > 0) {
      periodAtt.FilterLookup.set(null, '[Blank]')
      const retailWindows = catalog.ShipmentWindowRangeList.filter(s => utils.isValidStringValue(s.Period))
      const periods = uniqBy(retailWindows, 'Period').map(a => a.Period).sort((a, b) => a.localeCompare(b))
      periods.forEach((period) => {
        periodAtt.FilterLookup.set(period.toLowerCase(), period)
      })
    }
    const seasonAtt = attDic._Seasons
    seasonAtt?.FilterLookup.clear()
    if (seasonAtt && Object.keys(linkedCatalogDetails).length) {
      const indexedSeasonValueCount = Object.values(linkedCatalogDetails).reduce((acu, linkedCatalog) => {
        const seasonValue = utils.isValidStringValue(linkedCatalog.Season) ? linkedCatalog.Season : '[Blank]'
        if (!utils.isDefined(acu[seasonValue])) {
          acu[seasonValue] = 0
        }
        acu[seasonValue]++
        return acu
      }, {} as Record<string, number>)

      Object.values(linkedCatalogDetails).forEach((linkedCatalog) => {
        const seasonValue = utils.isValidStringValue(linkedCatalog.Season) ? linkedCatalog.Season : '[Blank]'
        seasonAtt.FilterLookup.set(linkedCatalog.CatalogCode, indexedSeasonValueCount[seasonValue] <= 1 ? seasonValue : `${seasonValue} (${linkedCatalog.CatalogCode})`)
      })
      seasonAtt.AllowFiltering = seasonAtt.FilterLookup.size > 0
    }

    if (catalog.RequestsEnabled > 0) {
      if (catalog.RequestAttributeList && catalog.RequestAttributeList.length) {
        catalog.RequestAttributeList.forEach((att) => {
          if (att.Status > 0) {
            const parsedValidationExpression = utils.tryParse(att.ValidationExpression)
            const newAtt: IMyAttribute = {
              AttributeType: att.AttributeTypeId,
              AttributeSource: 'request',
              Creatable: true,
              DisplayName: att.AttributeDisplayName,
              Editable: att.AttributeTypeId !== AttributeType.Calc, // for calc type, editable is false
              ReadOnly: att.AttributeTypeId === AttributeType.Calc, // for calc type ReadOnly = true
              AllowFiltering: true,
              FilterLookup: new Map(),
              IsRequired: false,
              IsSeasonless: false,
              Overridable: false,
              IsStatic: false,
              IsPersonal: false,
              IsRequest: true,
              SystemName: att.AttributeSystemName,
              VettingList: catalog.Config.RequestAttributesVettingList[att.AttributeSystemName],
              ValidationExpression: att.ValidationExpression,
              ValidationMessage: att.ValidationMessage,
              parsedValidationExpression: parsedValidationExpression && parsedValidationExpression.hasOwnProperty('F') && isObject(parsedValidationExpression.F) ? parsedValidationExpression.F : undefined,
              Visible: true,
              IsModelLevel: false,
              Criteria: {},
              VisibleInListByDefault: false,
            }

            if (newAtt.VettingList && newAtt.VettingList.length) {
              newAtt.VettingList.forEach((value) => {
                const key = value.toLowerCase()
                if (!newAtt.FilterLookup.has(null)) {
                  newAtt.FilterLookup.set(null, '[Blank]')
                }
                if (value && !newAtt.FilterLookup.has(key)) {
                  newAtt.FilterLookup.set(key, value)
                }
              })
            }

            if (newAtt.AttributeType === AttributeType.Calc && parsedValidationExpression && parsedValidationExpression.hasOwnProperty('calculatedRegexExpression')) {
              const calculatedFormula = parsedValidationExpression.calculatedRegexExpression.calculatedFormula
              if (utils.isValidStringValue(calculatedFormula)) {
                // eslint-disable-next-line no-new-func
                newAtt._calcFunction = Function('data', `'use strict';return (${calculatedFormula})`)
              }
            }

            attDic[newAtt.SystemName] = newAtt
          }
        })
      }
    }

    return attDic
  }

  async buildResourceTree(catalogCode: number): Promise<ITreeNode[]> {
    const resources = (await this.resources.where({ CatalogCode: catalogCode }).toArray())
      .sort((a, b) => {
        let result = a.SortOrder - b.SortOrder
        if (result === 0) {
          const aResourceCategoryName: string = a.ResourceCategory.trim().toLowerCase() + a.ResourceName.trim().toLowerCase()
          const bResourceCategoryName: string = b.ResourceCategory.trim().toLowerCase() + b.ResourceName.trim().toLowerCase()
          result = aResourceCategoryName.localeCompare(bResourceCategoryName)
        }
        return result
      })

    const tree: ITreeNode[] = []
    const resourcesCategoryIndex = {}
    resources.forEach((resource) => {
      if (resource.Status > 0 && resource.ResourceVisibleInResourceList > 0) {
        const resourceCategory = resource.ResourceCategory?.trim().toLowerCase() || 'others'
        if (!resourcesCategoryIndex.hasOwnProperty(resourceCategory)) {
          tree.push({
            key: resourceCategory,
            label: resourceCategory,
            path: [],
            checked: false,
            expanded: false,
            sortOrder: 0,
            children: [],
            actions: [],
          })
          resourcesCategoryIndex[resourceCategory] = tree.length - 1
        }
        tree[resourcesCategoryIndex[resourceCategory]].children.push({
          key: resource.ResourceId,
          label: resource.ResourceName?.trim().toLowerCase(),
          path: [],
          checked: false,
          expanded: false,
          sortOrder: 0,
          children: [],
          actions: [],
        })
      }
    })
    return tree
  }

  async getResourceTypes(): Promise<ResourceType[]> {
    try {
      const res = await getResourceTypes()
      const data = res.data.map((itm: any) => new ResourceType(itm))
      await this.resourceTypes.bulkPut(data)
    }
    catch (error) {
      console.warn('Unable to get resource types', error)
    }
    return await this.resourceTypes.toArray()
  }

  async getRelationTypes(catalogCode: number): Promise<RelationType[]> {
    try {
      const res = await getRelationTypes(catalogCode)
      const data = res.data.map((itm: any) => new RelationType(catalogCode, itm))
      await this.relationTypes.bulkPut(data)
      await this.relationTypes.where('Status').equals(0).delete()
    }
    catch (error) {
      console.warn('Unable to get relation types', error)
    }
    return await this.relationTypes.where({ CatalogCode: catalogCode }).toArray()
  }

  async getObjectRelations(catalogCode: number, objectId: number, entityId: number, isRequestArticle: ZeroOrOneType = 0): Promise<ObjectRelation[]> {
    try {
      const res = await getObjectRelations(catalogCode, objectId, entityId, isRequestArticle)
      const data = res.data.map((itm: any) => new ObjectRelation(catalogCode, objectId, entityId, itm))
      await this.objectRelations.bulkPut(data)
    }
    catch (error) {
      console.warn('Unable to get object relations', error)
    }
    return await this.objectRelations.where({ CatalogCode: catalogCode, ObjectId: objectId, EntityId: entityId, IsRequestArticle: isRequestArticle }).toArray()
  }

  // delete jobs older than 1 day
  async deleteOlderJobs() {
    try {
      const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000
      await this.jobs
        .where('CreatedDate')
        .below(oneDayAgo)
        .delete()
    }
    catch (error) {
      console.warn('Error deleting old jobs', error)
    }
  }
}
